import { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Lock, Search, Plus, X, Phone, Mail, MapPin, Calendar, User, 
  MessageSquare, TrendingUp, ChevronRight, ChevronDown, ChevronUp,
  Clock, Target, AlertTriangle, CheckCircle, XCircle, Star, Filter,
  RefreshCw, Trash2, Edit3, Save, ArrowLeft, BarChart3, PhoneCall,
  PhoneOff, CalendarPlus, UserCheck, AlertCircle, Zap, Eye, Bell,
  Menu, Home, FileText, Users, Settings, LogOut, Instagram, MessageCircle,
  Flame, Snowflake, CircleDot, BellRing, Brain, Sparkles, TrendingDown,
  CalendarDays
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { ActionPlanSection, StrengthsWeaknessesSection } from '../components/ActionPlanSection';
import SalesAnalysisTable from '../components/SalesAnalysisTable';
import PRSNLAccompagnementSection from '../components/PRSNLAccompagnementSection';
import { pushService } from '../services/pushNotifications';
import { CalendarView, ScheduleCallModal, CallHistory } from '../components/CalendarView';
import { NotificationContainer, useCallReminders, getScheduledCallNotification, getWhatsAppReminderTemplate } from '../components/NotificationSystem';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Theme colors
const theme = {
  bg: '#F7F4EF',
  bg2: '#EDE9E1',
  bg3: '#E0DBD0',
  ink: '#1A1713',
  ink2: '#3D3830',
  ink3: '#6B6358',
  ink4: '#9A9187',
  ink5: '#C8C0B4',
  orange: '#E8621A',
  orange2: '#F5894A',
  green: '#1D7A4F',
  red: '#C0392B',
  blue: '#1A4A8A',
  amber: '#B8760A',
  purple: '#7c3aed',
  hot: '#EF4444',
  warm: '#F59E0B',
  cold: '#3B82F6',
};

// NOUVEAUX STATUTS CLAIRS - Pipeline complet avec couleurs distinctes
const STATUSES = [
  { id: 'nouveau', label: '🆕 Nouveau', color: '#6B7280', action: 'À traiter', icon: Bell },
  { id: 'contacte', label: '📞 Contacté', color: '#3B82F6', action: 'Suivre', icon: Phone },
  { id: 'rdv_programme', label: '📅 RDV programmé', color: '#8B5CF6', action: 'Attendre', icon: Calendar },
  { id: 'appel_effectue', label: '✅ Appel effectué', color: '#10B981', action: 'Closer', icon: PhoneCall },
  { id: 'gagne', label: '🏆 Gagné', color: '#059669', action: 'Célébrer!', icon: CheckCircle },
  { id: 'perdu', label: '❌ Perdu', color: '#DC2626', action: 'Archivé', icon: XCircle },
  { id: 'no_show', label: '👻 No show', color: '#F97316', action: 'Relancer', icon: AlertCircle },
];

// Score level definitions with temperature
const getTemperature = (score, diag) => {
  // Logique basée sur le score et les problèmes
  const hasHighFoodCost = diag?.fc && parseInt(diag.fc) > 35;
  const hasLowMargin = diag?.ebe && parseInt(diag.ebe.replace(/[^0-9-]/g, '')) < 2000;
  const hasPrimeCostIssue = diag?.prime_cost && parseInt(diag.prime_cost) > 65;
  
  // Lead chaud = score bas + problèmes financiers évidents
  if (score <= 40 || hasHighFoodCost || hasLowMargin || hasPrimeCostIssue) {
    return { level: 'hot', label: 'CHAUD', emoji: '🔥', color: theme.hot, bgColor: 'rgba(239,68,68,0.1)' };
  }
  // Lead moyen = score moyen ou quelques problèmes
  if (score <= 60) {
    return { level: 'warm', label: 'MOYEN', emoji: '🟡', color: theme.warm, bgColor: 'rgba(245,158,11,0.1)' };
  }
  // Lead froid = score élevé, peu de problèmes évidents
  return { level: 'cold', label: 'FROID', emoji: '❄️', color: theme.cold, bgColor: 'rgba(59,130,246,0.1)' };
};

// Get main problem from diagnostic
const getMainProblem = (diag, allAnswers) => {
  const problems = [];
  
  // Check food cost
  if (diag?.fc) {
    const fc = parseInt(diag.fc);
    if (fc > 35) problems.push({ text: 'Food cost élevé', severity: 'high', icon: '⚠️' });
    else if (fc > 30) problems.push({ text: 'Food cost à optimiser', severity: 'medium', icon: '📊' });
  } else if (diag?.fc === 'Inconnu') {
    problems.push({ text: 'Food cost inconnu', severity: 'high', icon: '❓' });
  }
  
  // Check margin
  if (diag?.ebe) {
    const ebe = parseInt(diag.ebe.replace(/[^0-9-]/g, ''));
    if (ebe < 0) problems.push({ text: 'EBE négatif', severity: 'critical', icon: '🚨' });
    else if (ebe < 2000) problems.push({ text: 'Marge faible', severity: 'high', icon: '💸' });
  }
  
  // Check prime cost
  if (diag?.prime_cost) {
    const pc = parseInt(diag.prime_cost);
    if (pc > 70) problems.push({ text: 'Prime cost critique', severity: 'critical', icon: '🔴' });
    else if (pc > 65) problems.push({ text: 'Prime cost élevé', severity: 'high', icon: '⚠️' });
  }
  
  // Check answers for other issues
  if (allAnswers) {
    if (allAnswers.autonomie === 'pas_1_jour') {
      problems.push({ text: 'Dépend du patron', severity: 'medium', icon: '⏱' });
    }
    if (allAnswers.process === 'tete') {
      problems.push({ text: 'Pas de process', severity: 'medium', icon: '📝' });
    }
    if (allAnswers.connait_foodcost === 'non') {
      problems.push({ text: 'Ne suit pas ses chiffres', severity: 'high', icon: '📉' });
    }
  }
  
  return problems.length > 0 ? problems[0] : null;
};

// Calculate estimated loss
const calculateEstimatedLoss = (diag) => {
  if (!diag?.ca) return null;
  
  const ca = parseInt(diag.ca.replace(/[^0-9]/g, ''));
  if (!ca) return null;
  
  let loss = 0;
  
  // Food cost excess
  if (diag?.fc && diag.fc !== 'Inconnu') {
    const fc = parseInt(diag.fc);
    if (fc > 30) {
      loss += Math.round(ca * (fc - 30) / 100);
    }
  }
  
  // Prime cost excess
  if (diag?.prime_cost && diag.prime_cost !== 'Inconnu') {
    const pc = parseInt(diag.prime_cost);
    if (pc > 60) {
      loss += Math.round(ca * (pc - 60) / 100 * 0.5); // Partial overlap with food cost
    }
  }
  
  return loss > 0 ? loss : null;
};

const getStatus = (id) => STATUSES.find(s => s.id === id) || STATUSES[0];

// Format helpers
const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
};

const formatDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const now = new Date();
  const date = new Date(iso);
  const diff = Math.floor((now - date) / 1000 / 60);
  if (diff < 1) return 'à l\'instant';
  if (diff < 60) return `il y a ${diff}min`;
  if (diff < 1440) return `il y a ${Math.floor(diff / 60)}h`;
  if (diff < 10080) return `il y a ${Math.floor(diff / 1440)}j`;
  return formatDate(iso);
};

// Access Code
const CRM_CODE = 'PRSNLSABRIL';
// Bump this version whenever the access code is rotated → invalidates all existing sessions
const CRM_AUTH_VERSION = '2026-02-20-v2';

// Lock Screen
const LockScreen = ({ onUnlock }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (code.toUpperCase() === CRM_CODE) {
      localStorage.setItem('crm_unlocked', 'true');
      localStorage.setItem('crm_auth_version', CRM_AUTH_VERSION);
      onUnlock();
    } else {
      setError(true);
      setTimeout(() => setError(false), 2000);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: theme.bg }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200 max-w-md w-full text-center"
      >
        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: 'rgba(232,98,26,0.1)' }}>
          <Lock className="w-8 h-8" style={{ color: theme.orange }} />
        </div>
        <h1 className="font-bebas text-3xl text-gray-800 mb-2">PRSNL CRM</h1>
        <p className="text-gray-500 mb-6 text-sm">Accès réservé à l'équipe commerciale</p>
        
        <form onSubmit={handleSubmit}>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Code d'accès"
            className={`w-full px-4 py-3 rounded-xl border-2 text-center font-bold tracking-widest uppercase outline-none transition-colors ${
              error ? 'border-red-500 bg-red-50' : 'border-gray-200 focus:border-orange-500'
            }`}
            data-testid="crm-code-input"
          />
          {error && <p className="text-red-500 text-sm mt-2">Code incorrect</p>}
          <button
            type="submit"
            className="w-full mt-4 py-3 rounded-xl font-bold text-white transition-all hover:scale-105"
            style={{ backgroundColor: theme.orange }}
            data-testid="crm-unlock-btn"
          >
            ACCÉDER AU CRM
          </button>
        </form>
        
        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-gray-500 text-sm mb-3">Membre de l'équipe ?</p>
          <Link 
            to="/connexion"
            className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-orange-600 bg-orange-50 border border-orange-200 hover:bg-orange-100 transition-colors"
          >
            <User className="w-4 h-4" />
            Se connecter avec un compte
          </Link>
        </div>
        
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-gray-400 hover:text-gray-600 text-sm mt-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour à l'accueil
        </Link>
      </motion.div>
    </div>
  );
};

// Temperature Badge Component
const TemperatureBadge = ({ temperature, small = false }) => (
  <span 
    className={`inline-flex items-center gap-1 font-bold rounded-full ${small ? 'px-2 py-0.5 text-[10px]' : 'px-3 py-1 text-xs'}`}
    style={{ 
      backgroundColor: temperature.bgColor,
      color: temperature.color,
      border: `1px solid ${temperature.color}30`
    }}
  >
    <span>{temperature.emoji}</span>
    <span>{temperature.label}</span>
  </span>
);

// Problem Badge Component
const ProblemBadge = ({ problem }) => {
  if (!problem) return null;
  
  const severityColors = {
    critical: { bg: 'rgba(220,38,38,0.1)', color: '#DC2626', border: 'rgba(220,38,38,0.3)' },
    high: { bg: 'rgba(245,158,11,0.1)', color: '#D97706', border: 'rgba(245,158,11,0.3)' },
    medium: { bg: 'rgba(59,130,246,0.1)', color: '#2563EB', border: 'rgba(59,130,246,0.3)' },
  };
  
  const style = severityColors[problem.severity] || severityColors.medium;
  
  return (
    <span 
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold"
      style={{ backgroundColor: style.bg, color: style.color, border: `1px solid ${style.border}` }}
    >
      {problem.icon} {problem.text}
    </span>
  );
};

// Last Action Badge
const LastActionBadge = ({ lead }) => {
  // Determine last action
  let lastAction = null;
  let actionTime = null;
  
  if (lead.last_action_at) {
    lastAction = lead.last_action || 'Action';
    actionTime = lead.last_action_at;
  } else if (lead.setter?.contacted_at) {
    lastAction = 'Appelé';
    actionTime = lead.setter.contacted_at;
  } else if (lead.is_new) {
    lastAction = 'Nouveau lead';
    actionTime = lead.created_at;
  }
  
  if (!lastAction) return null;
  
  return (
    <span className="text-[10px] text-gray-500 flex items-center gap-1">
      <Clock className="w-3 h-3" />
      {lastAction} {timeAgo(actionTime)}
    </span>
  );
};

// Quick Action Button
const QuickActionBtn = ({ icon: Icon, label, color, onClick, small, active }) => (
  <motion.button
    whileTap={{ scale: 0.95 }}
    onClick={onClick}
    className={`flex items-center gap-1.5 rounded-lg font-semibold transition-all ${
      small ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm'
    } ${active ? 'ring-2 ring-offset-1' : ''}`}
    style={{ 
      backgroundColor: active ? color : `${color}15`,
      color: active ? '#FFF' : color,
      border: `1px solid ${color}30`,
      ringColor: color
    }}
  >
    <Icon className={small ? 'w-3 h-3' : 'w-4 h-4'} />
    {label}
  </motion.button>
);

// Status Badge
const StatusBadge = ({ status, onClick, active }) => {
  const s = getStatus(status);
  const Icon = s.icon;
  return (
    <motion.button
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
        active ? 'ring-2 ring-offset-1' : ''
      }`}
      style={{ 
        backgroundColor: active ? s.color : `${s.color}15`,
        color: active ? '#FFF' : s.color,
        ringColor: s.color
      }}
    >
      <Icon className="w-3 h-3" />
      {s.label}
    </motion.button>
  );
};

// Lead Card Component - UPGRADED avec couleurs intelligentes et actions
const LeadCard = ({ lead, onSelect, onStatusChange, onQuickAction, onUpdateField }) => {
  const [showStatusSelector, setShowStatusSelector] = useState(false);
  const [previousStatus, setPreviousStatus] = useState(lead.status);
  
  const temperature = getTemperature(lead.score, lead.diag);
  const mainProblem = getMainProblem(lead.diag, lead.all_answers);
  const status = getStatus(lead.status);
  const Icon = status.icon;
  
  // WhatsApp link
  const whatsappLink = lead.phone ? `https://wa.me/${lead.phone.replace(/[^0-9]/g, '')}` : null;
  
  // Instagram search link (recherche Google -> Instagram car l'ancienne URL ne fonctionne plus)
  const instagramSearchLink = `https://www.google.com/search?q=site:instagram.com+${encodeURIComponent(lead.restaurant || '')}+restaurant`;
  
  // WhatsApp Business link with pre-filled message
  const whatsappNumber = "33745522450";
  const whatsappMessage = encodeURIComponent(`Salut ! Je te contacte pour ${lead.restaurant || 'ton restaurant'}. J'ai vu ton audit et j'aimerais en parler avec toi.`);
  const whatsappBusinessLink = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;
  
  // Direct WhatsApp link for lead's phone
  const getLeadWhatsAppLink = () => {
    if (!lead.phone) return null;
    const cleanPhone = lead.phone.replace(/[^0-9+]/g, '').replace(/^\+/, '');
    const message = encodeURIComponent(`Salut ! Je suis de l'équipe PRSNL. J'ai regardé ton audit pour ${lead.restaurant || 'ton restaurant'} et j'ai quelques recommandations à te partager. Tu as 2 min ?`);
    return `https://wa.me/${cleanPhone}?text=${message}`;
  };
  const leadWhatsAppLink = getLeadWhatsAppLink();
  
  // Calculate activity stats
  const calls = lead.activity?.calls || 0;
  const messages = lead.activity?.messages || 0;
  const pickups = lead.activity?.pickups || 0;
  
  // Calculate time since last action
  const getTimeSinceAction = () => {
    const lastAction = lead.last_action_at || lead.created_at;
    if (!lastAction) return { hours: 0, isUrgent: false };
    const diff = (new Date() - new Date(lastAction)) / (1000 * 60 * 60);
    return { hours: Math.floor(diff), isUrgent: diff > 24 };
  };
  const timeSince = getTimeSinceAction();
  
  // Check if lead is untreated (no actions)
  const isUntreated = calls === 0 && messages === 0 && !lead.last_action_at;
  
  // Calculate potential gain
  const ca = lead.diag?.ca ? parseInt(String(lead.diag.ca).replace(/[^0-9]/g, '')) : 50000;
  const potentialGain = Math.round(ca * 0.15);
  
  // Determine card priority color
  const getCardStyle = () => {
    if (status.id === 'gagne') return { border: 'border-green-400', bg: 'bg-gradient-to-br from-green-50 to-emerald-50', glow: '' };
    if (status.id === 'perdu') return { border: 'border-gray-300', bg: 'bg-gray-50 opacity-60', glow: '' };
    if (isUntreated || timeSince.isUrgent) return { border: 'border-red-400', bg: 'bg-white', glow: 'shadow-[0_0_15px_rgba(239,68,68,0.3)] animate-pulse' };
    if (status.id === 'rdv_pris') return { border: 'border-blue-400', bg: 'bg-blue-50', glow: '' };
    if (status.id === 'rdv_fait') return { border: 'border-purple-400', bg: 'bg-purple-50', glow: '' };
    if (status.id === 'relance') return { border: 'border-amber-400', bg: 'bg-amber-50', glow: '' };
    if (lead.is_new) return { border: 'border-blue-400', bg: 'bg-blue-50', glow: '' };
    return { border: 'border-gray-200', bg: 'bg-white', glow: '' };
  };
  const cardStyle = getCardStyle();
  
  // Handle status change with toggle
  const handleStatusChange = (newStatus) => {
    if (lead.status === newStatus) {
      // Toggle: re-click = reset to previous
      onStatusChange(lead.id, previousStatus !== newStatus ? previousStatus : 'nouveau');
    } else {
      setPreviousStatus(lead.status);
      onStatusChange(lead.id, newStatus);
    }
    setShowStatusSelector(false);
  };
  
  // Determine next action
  const getNextAction = () => {
    if (isUntreated) return { label: 'Appeler en priorité', icon: Phone, color: theme.red };
    if (status.id === 'relance') return { label: 'Relancer WhatsApp', icon: MessageCircle, color: theme.amber };
    if (status.id === 'rdv_pris') return { label: 'Confirmer RDV', icon: Calendar, color: theme.blue };
    if (status.id === 'rdv_fait') return { label: 'Closer maintenant', icon: Target, color: theme.purple };
    if (timeSince.isUrgent) return { label: 'Relancer urgent', icon: AlertTriangle, color: theme.red };
    return { label: 'Appeler', icon: Phone, color: theme.green };
  };
  const nextAction = getNextAction();
  
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-xl border-2 overflow-hidden transition-all ${cardStyle.border} ${cardStyle.bg} ${cardStyle.glow}`}
      data-testid={`lead-card-${lead.id}`}
    >
      {/* Header with status + urgency indicator */}
      <div 
        className="px-3 py-2 flex items-center justify-between cursor-pointer"
        style={{ backgroundColor: `${status.color}15` }}
        onClick={() => setShowStatusSelector(!showStatusSelector)}
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">{STATUSES.find(s => s.id === lead.status)?.label.split(' ')[0] || '🔵'}</span>
          <span className="text-xs font-black uppercase tracking-wide" style={{ color: status.color }}>
            {status.action}
          </span>
          {lead.is_new && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-500 text-white animate-pulse flex items-center gap-1">
              <Zap className="w-3 h-3" />
              NEW
            </span>
          )}
          {isUntreated && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-500 text-white animate-pulse">
              ⚠️ NON TRAITÉ
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {timeSince.isUrgent && (
            <span className="text-[10px] font-bold text-red-600">⏰ +24h</span>
          )}
          <span className={`text-[10px] flex items-center gap-1 ${timeSince.isUrgent ? 'text-red-500 font-bold' : 'text-gray-500'}`}>
            <Clock className="w-3 h-3" />
            {timeSince.hours < 1 ? 'Maintenant' : `il y a ${timeSince.hours}h`}
          </span>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showStatusSelector ? 'rotate-180' : ''}`} />
        </div>
      </div>
      
      {/* STATUS SELECTOR - Dropdown avec tous les statuts */}
      <AnimatePresence>
        {showStatusSelector && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-b border-gray-200"
          >
            <div className="p-2 bg-gray-50">
              <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">STATUT</p>
              <div className="flex flex-wrap gap-1.5">
                {STATUSES.map(s => (
                  <button
                    key={s.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStatusChange(s.id);
                    }}
                    className={`px-2 py-1 rounded-lg text-[11px] font-bold transition-all active:scale-95 flex items-center gap-1 ${
                      lead.status === s.id 
                        ? 'ring-2 ring-offset-1' 
                        : 'hover:opacity-80'
                    }`}
                    style={{ 
                      backgroundColor: `${s.color}20`,
                      color: s.color,
                      ringColor: lead.status === s.id ? s.color : 'transparent'
                    }}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Main content */}
      <div className="p-3">
        {/* Temperature + Problem + Potentiel € */}
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <TemperatureBadge temperature={temperature} small />
          {mainProblem && <ProblemBadge problem={mainProblem} />}
          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-700 border border-green-300">
            💰 +{Math.round(potentialGain/1000)}k€/mois
          </span>
        </div>
        
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onSelect(lead)}>
            <h3 className="font-bold text-gray-800 truncate hover:text-orange-600 transition-colors" data-testid={`lead-name-${lead.id}`}>
              {lead.restaurant}
            </h3>
            <p className="text-sm text-gray-500 truncate">
              {lead.owner || <span className="text-amber-600 font-medium">⚠️ Infos manquantes</span>}
            </p>
            {lead.phone && (
              <a 
                href={`tel:${lead.phone}`}
                className="inline-flex items-center gap-1 text-xs text-blue-600 mt-0.5 hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                <Phone className="w-3 h-3" />
                {lead.phone}
              </a>
            )}
          </div>
          
          {/* Score indicator */}
          <div 
            className="flex flex-col items-center px-3 py-2 rounded-lg"
            style={{ backgroundColor: temperature.bgColor }}
          >
            <span className="text-2xl font-black" style={{ color: temperature.color }}>
              {lead.score}
            </span>
            <span className="text-[10px] font-medium" style={{ color: temperature.color }}>
              /100
            </span>
          </div>
        </div>
        
        {/* Activity badges - KPI rapides */}
        <div className="flex items-center gap-2 mb-2 p-2 rounded-lg bg-gray-50">
          <span className={`text-[10px] font-bold flex items-center gap-1 ${calls === 0 ? 'text-red-500' : 'text-gray-600'}`}>
            📞 {calls}
          </span>
          <span className={`text-[10px] font-bold flex items-center gap-1 ${messages === 0 ? 'text-red-500' : 'text-gray-600'}`}>
            💬 {messages}
          </span>
          <span className="text-[10px] font-bold text-gray-600 flex items-center gap-1">
            ✅ {pickups}
          </span>
          <span className="text-[10px] text-gray-400 ml-auto">
            Taux: {calls > 0 ? Math.round(pickups/calls*100) : 0}%
          </span>
        </div>
        
        {/* Quick info pills */}
        {lead.diag && (
          <div className="flex flex-wrap gap-1 mb-2">
            {lead.diag.ca && (
              <span className="px-2 py-0.5 bg-gray-100 rounded text-[10px] text-gray-600 font-medium">
                CA: {lead.diag.ca}
              </span>
            )}
            {lead.diag.fc && lead.diag.fc !== 'Inconnu' && (
              <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${parseInt(lead.diag.fc) > 30 ? 'bg-red-100 text-red-600' : 'bg-green-50 text-green-600'}`}>
                FC: {lead.diag.fc}
              </span>
            )}
          </div>
        )}
        
        {/* PROCHAINE ACTION - très visible */}
        <div 
          className="p-2 rounded-lg mb-2 flex items-center justify-between"
          style={{ backgroundColor: `${nextAction.color}15`, border: `1px solid ${nextAction.color}30` }}
        >
          <div className="flex items-center gap-2">
            <nextAction.icon className="w-4 h-4" style={{ color: nextAction.color }} />
            <span className="text-xs font-bold" style={{ color: nextAction.color }}>
              👉 {nextAction.label}
            </span>
          </div>
        </div>
        
        {/* Quick actions - avec WhatsApp et Instagram fonctionnels */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <QuickActionBtn 
            icon={Phone} 
            label="Appeler" 
            color={theme.green} 
            small
            onClick={(e) => {
              e.stopPropagation();
              if (lead.phone) {
                window.location.href = `tel:${lead.phone}`;
                const activity = lead.activity || { calls: 0, pickups: 0, messages: 0 };
                onUpdateField && onUpdateField(lead.id, 'activity', { ...activity, calls: activity.calls + 1 });
                onUpdateField && onUpdateField(lead.id, 'last_action_at', new Date().toISOString());
              }
            }}
          />
          {whatsappLink && (
            <QuickActionBtn 
              icon={MessageCircle} 
              label="WhatsApp" 
              color="#25D366" 
              small
              onClick={(e) => {
                e.stopPropagation();
                window.open(whatsappLink, '_blank');
                const activity = lead.activity || { calls: 0, pickups: 0, messages: 0 };
                onUpdateField && onUpdateField(lead.id, 'activity', { ...activity, messages: activity.messages + 1 });
                onUpdateField && onUpdateField(lead.id, 'last_action_at', new Date().toISOString());
              }}
            />
          )}
          <QuickActionBtn 
            icon={Instagram} 
            label="Insta" 
            color="#E4405F" 
            small
            onClick={(e) => {
              e.stopPropagation();
              window.open(instagramSearchLink, '_blank');
            }}
          />
          {/* WhatsApp button - contact lead directly */}
          {leadWhatsAppLink && (
            <QuickActionBtn 
              icon={MessageCircle} 
              label="WhatsApp" 
              color="#25D366" 
              small
              onClick={(e) => {
                e.stopPropagation();
                window.open(leadWhatsAppLink, '_blank');
              }}
            />
          )}
          <QuickActionBtn 
            icon={RefreshCw} 
            label="Relancer" 
            color={theme.amber} 
            small
            onClick={(e) => {
              e.stopPropagation();
              handleStatusChange('relance');
            }}
          />
          <button
            onClick={() => onSelect(lead)}
            className="ml-auto text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"
            data-testid={`lead-details-btn-${lead.id}`}
          >
            Détails <ChevronRight className="w-3 h-3" />
          </button>
        </div>
        
        {/* QUICK STATUS BUTTONS - Boutons de statut directement sur la carte */}
        <div className="mt-2 pt-2 border-t border-gray-100">
          <p className="text-[9px] text-gray-400 uppercase tracking-wider mb-1.5">Statut rapide :</p>
          <div className="flex flex-wrap gap-1">
            {[
              { id: 'a_contacter', emoji: '📞', label: 'À contacter' },
              { id: 'tentative', emoji: '📲', label: 'Tentative' },
              { id: 'rdv_pris', emoji: '📅', label: 'RDV pris' },
              { id: 'gagne', emoji: '💰', label: 'Closé' },
              { id: 'perdu', emoji: '❌', label: 'Perdu' },
            ].map(s => {
              const statusInfo = STATUSES.find(st => st.id === s.id);
              const isActive = lead.status === s.id;
              return (
                <button
                  key={s.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStatusChange(s.id);
                  }}
                  className={`px-2 py-1 rounded text-[10px] font-bold transition-all active:scale-95 ${
                    isActive ? 'ring-2 ring-offset-1' : 'hover:opacity-80'
                  }`}
                  style={{
                    backgroundColor: isActive ? statusInfo?.color : `${statusInfo?.color}15`,
                    color: isActive ? '#FFF' : statusInfo?.color,
                    ringColor: statusInfo?.color
                  }}
                  data-testid={`lead-quick-status-${s.id}-${lead.id}`}
                >
                  {s.emoji} {isActive ? '✓' : ''}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* RDV indicator - ENHANCED VERSION */}
      {lead.calendly_event && (() => {
        const eventTime = new Date(lead.calendly_event.start_time);
        const now = new Date();
        const diffMs = eventTime - now;
        const diffMins = Math.floor(diffMs / (1000 * 60));
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        
        // Determine status
        let rdvStatus = 'upcoming'; // upcoming, imminent, in_progress, past
        let statusLabel = '';
        let statusColor = '';
        let isHot = false;
        
        if (diffMs < 0) {
          rdvStatus = 'past';
          statusLabel = '✅ Passé';
          statusColor = 'bg-gray-100 text-gray-600';
        } else if (diffMins <= 15) {
          rdvStatus = 'in_progress';
          statusLabel = '🔴 EN COURS';
          statusColor = 'bg-red-100 text-red-700 animate-pulse';
        } else if (diffHours < 1) {
          rdvStatus = 'imminent';
          statusLabel = `⏳ Dans ${diffMins} min`;
          statusColor = 'bg-orange-100 text-orange-700';
          isHot = true;
        } else if (diffHours < 24) {
          rdvStatus = 'imminent';
          statusLabel = `⏳ Dans ${diffHours}h`;
          statusColor = 'bg-amber-100 text-amber-700';
          isHot = true;
        } else if (diffDays === 1) {
          statusLabel = '📅 Demain';
          statusColor = 'bg-blue-100 text-blue-700';
        } else {
          statusLabel = `📅 Dans ${diffDays}j`;
          statusColor = 'bg-blue-50 text-blue-600';
        }
        
        // Get meeting link from location
        const meetingLink = lead.calendly_event.location?.join_url || 
                          lead.calendly_event.location?.url ||
                          (lead.calendly_event.location?.type === 'google_meet' ? 
                            `https://meet.google.com/${lead.calendly_event.id?.slice(0, 10)}` : null);
        
        const isCanceled = lead.calendly_event.status === 'canceled';
        
        return (
          <div className={`px-3 py-2.5 border-t ${isCanceled ? 'bg-red-50 border-red-200' : isHot ? 'bg-gradient-to-r from-orange-50 to-red-50 border-orange-200' : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200'}`}>
            {isCanceled ? (
              <div className="flex items-center gap-2">
                <XCircle className="w-4 h-4 text-red-500" />
                <span className="text-xs font-bold text-red-600">❌ RDV ANNULÉ</span>
                <span className="text-[10px] text-red-500">Relance nécessaire</span>
              </div>
            ) : (
              <>
                {/* Hot lead badge */}
                {isHot && (
                  <div className="flex items-center gap-1 mb-1.5">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-gradient-to-r from-red-500 to-orange-500 text-white animate-pulse">
                      🔥 HOT - RDV IMMINENT
                    </span>
                  </div>
                )}
                
                {/* Main RDV info */}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 flex-1">
                    <Calendar className="w-4 h-4 text-blue-600" />
                    <div className="flex flex-col">
                      <span className="text-xs font-bold text-gray-800">
                        {eventTime.toLocaleDateString('fr-FR', {
                          weekday: 'short',
                          day: 'numeric',
                          month: 'short'
                        })} à {eventTime.toLocaleTimeString('fr-FR', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${statusColor}`}>
                      {statusLabel}
                    </span>
                  </div>
                  
                  {/* WhatsApp button instead of video meeting */}
                  {leadWhatsAppLink && rdvStatus !== 'past' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(leadWhatsAppLink, '_blank');
                      }}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold text-white transition-all hover:scale-105 active:scale-95 ${
                        rdvStatus === 'in_progress' 
                          ? 'bg-gradient-to-r from-green-500 to-green-600 animate-pulse' 
                          : 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700'
                      }`}
                      data-testid={`whatsapp-call-btn-${lead.id}`}
                    >
                      <MessageCircle className="w-4 h-4" />
                      {rdvStatus === 'in_progress' ? 'APPELER' : 'WhatsApp'}
                    </button>
                  )}
                </div>
                
                {/* Countdown for imminent meetings */}
                {rdvStatus === 'imminent' && diffMins > 0 && (
                  <div className="mt-1.5 flex items-center gap-1">
                    <Clock className="w-3 h-3 text-orange-500" />
                    <span className="text-[10px] font-medium text-orange-600">
                      Préparez votre closing ! Le lead est chaud 🔥
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        );
      })()}
      
      {/* Setter/Closer assignment tags */}
      {(lead.setter?.name || lead.closer?.name) && (
        <div className="px-3 py-1.5 border-t border-gray-100 bg-gradient-to-r from-amber-50 to-blue-50">
          <div className="flex flex-wrap gap-1.5">
            {lead.setter?.name && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700">
                <User className="w-3 h-3" />
                Setter: {lead.setter.name}
              </span>
            )}
            {lead.closer?.name && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700">
                <Target className="w-3 h-3" />
                Closer: {lead.closer.name}
              </span>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
};

// Stats Card
const StatsCard = ({ label, value, icon: Icon, color, onClick, active }) => (
  <motion.button
    whileTap={{ scale: 0.98 }}
    onClick={onClick}
    className={`flex-1 min-w-[80px] p-3 rounded-xl border transition-all ${
      active ? 'ring-2 ring-offset-2' : ''
    }`}
    style={{ 
      backgroundColor: active ? color : '#FFF',
      borderColor: active ? color : theme.bg3,
      ringColor: color
    }}
  >
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4" style={{ color: active ? '#FFF' : color }} />
      <span className="text-2xl font-black" style={{ color: active ? '#FFF' : theme.ink }}>
        {value}
      </span>
    </div>
    <p className="text-[10px] font-medium mt-1" style={{ color: active ? '#FFF' : theme.ink4 }}>
      {label}
    </p>
  </motion.button>
);

// Lead Detail Panel Component
const LeadDetailPanel = ({ 
  lead, 
  onClose, 
  onStatusChange, 
  onWorkflowUpdate, 
  onAddComment, 
  onDelete,
  onUpdateField,
  teamMembers = [],
  onScheduleCall,
  onMarkCallOutcome
}) => {
  const [commentAuthor, setCommentAuthor] = useState('setter');
  const [commentText, setCommentText] = useState('');
  const [whyWillBuy, setWhyWillBuy] = useState(lead.why_will_buy || '');
  const [viewMode, setViewMode] = useState('complete'); // 'simple' or 'complete'
  const [hasChanges, setHasChanges] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(lead.status);
  const [previousStatus, setPreviousStatus] = useState(lead.status);
  const [isSaving, setIsSaving] = useState(false);
  
  // Expert diagnosis state
  const [expertDiagnosis, setExpertDiagnosis] = useState(null);
  const [loadingDiagnosis, setLoadingDiagnosis] = useState(false);
  const [showExpertPanel, setShowExpertPanel] = useState(false);
  
  // Fetch expert diagnosis
  const fetchExpertDiagnosis = async () => {
    if (expertDiagnosis) {
      setShowExpertPanel(true);
      return;
    }
    setLoadingDiagnosis(true);
    try {
      const res = await fetch(`${API_URL}/api/expert/diagnosis/${lead.id}`);
      const data = await res.json();
      setExpertDiagnosis(data);
      setShowExpertPanel(true);
    } catch (err) {
      console.error('Failed to fetch diagnosis:', err);
    }
    setLoadingDiagnosis(false);
  };
  
  const temperature = getTemperature(lead.score, lead.diag);
  const mainProblem = getMainProblem(lead.diag, lead.all_answers);
  const estimatedLoss = calculateEstimatedLoss(lead.diag);
  
  // Track status changes
  const handleStatusChangeLocal = (newStatus) => {
    if (currentStatus === newStatus) {
      // Toggle: re-click = reset to previous
      setCurrentStatus(previousStatus !== newStatus ? previousStatus : 'nouveau');
    } else {
      setPreviousStatus(currentStatus);
      setCurrentStatus(newStatus);
    }
    setHasChanges(true);
  };
  
  // Save changes
  const saveChanges = async () => {
    setIsSaving(true);
    try {
      if (currentStatus !== lead.status) {
        await onStatusChange(lead.id, currentStatus);
      }
      setHasChanges(false);
    } catch (err) {
      console.error('Failed to save:', err);
    }
    setIsSaving(false);
  };
  
  // AUTO-CALCULATE CLOSING SCORE based on KPIs
  const calculateClosingScore = () => {
    let score = 30; // Base score
    
    // Activity points (max 25)
    const calls = lead.activity?.calls || 0;
    const pickups = lead.activity?.pickups || 0;
    const messages = lead.activity?.messages || 0;
    if (calls > 0) score += Math.min(calls * 3, 10);
    if (pickups > 0) score += Math.min(pickups * 5, 10);
    if (messages > 0) score += Math.min(messages * 2, 5);
    
    // Status points (max 25)
    if (lead.status === 'rdv_pris') score += 20;
    else if (lead.status === 'rdv_fait') score += 25;
    else if (lead.status === 'en_discussion') score += 10;
    
    // Lead quality points (max 20)
    if (lead.score >= 80) score += 15;
    else if (lead.score >= 60) score += 10;
    else if (lead.score >= 40) score += 5;
    
    // Has psychology filled (max 10)
    if (lead.psychology?.douleur) score += 3;
    if (lead.psychology?.objectif) score += 3;
    if (lead.psychology?.urgence === 'high' || lead.psychology?.urgence === 'critical') score += 4;
    
    return Math.min(score, 100);
  };
  
  const autoClosingScore = calculateClosingScore();
  
  // Get team members by role
  const setters = teamMembers.filter(m => m.role === 'setter');
  const closers = teamMembers.filter(m => m.role === 'closer');
  
  // Get member info by name
  const getMemberByName = (name) => teamMembers.find(m => m.name === name);
  const currentSetter = lead.setter?.name ? getMemberByName(lead.setter.name) : null;
  const currentCloser = lead.closer?.name ? getMemberByName(lead.closer.name) : null;
  
  // Instagram search link (recherche Google -> Instagram car l'ancienne URL ne fonctionne plus)
  const instagramSearchLink = `https://www.google.com/search?q=site:instagram.com+${encodeURIComponent(lead.restaurant || '')}+restaurant`;
  
  // WhatsApp Business link with pre-filled message
  const whatsappNumber = "33745522450";
  const whatsappMessage = encodeURIComponent(`Salut ! Je te contacte pour ${lead.restaurant || 'ton restaurant'}. J'ai vu ton audit et j'aimerais en parler avec toi.`);
  const whatsappBusinessLink = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;
  
  // Direct WhatsApp link for lead's phone
  const getLeadWhatsAppLink = () => {
    if (!lead.phone) return null;
    const cleanPhone = lead.phone.replace(/[^0-9+]/g, '').replace(/^\+/, '');
    const message = encodeURIComponent(`Salut ! Je suis de l'équipe PRSNL. J'ai regardé ton audit pour ${lead.restaurant || 'ton restaurant'} et j'ai quelques recommandations à te partager. Tu as 2 min ?`);
    return `https://wa.me/${cleanPhone}?text=${message}`;
  };
  const leadWhatsAppLink = getLeadWhatsAppLink();
  
  // Send payment link via WhatsApp
  const sendPaymentLink = () => {
    const phone = lead.phone?.replace(/[^0-9]/g, '');
    if (!phone) return alert('Pas de numéro de téléphone');
    
    const message = encodeURIComponent(
      `Bonjour ${lead.owner || ''} ! 👋\n\n` +
      `Suite à notre échange, voici le lien pour démarrer l'accompagnement PRSNL :\n\n` +
      `👉 https://audit-resto.fr/prsnl\n\n` +
      `Code d'accès : PRSNL2026\n\n` +
      `À tout de suite ! 🚀`
    );
    
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
  };
  
  // Generate strategic summary
  const getStrategicSummary = () => {
    const points = [];
    const diag = lead.diag || {};
    const answers = lead.all_answers || {};
    
    // Financial issues
    if (diag.fc && diag.fc !== 'Inconnu') {
      const fc = parseInt(diag.fc);
      if (fc > 35) points.push({ icon: '⚠️', text: 'Food cost trop élevé', color: theme.red });
      else if (fc > 30) points.push({ icon: '📊', text: 'Food cost à optimiser', color: theme.amber });
    } else if (diag.fc === 'Inconnu') {
      points.push({ icon: '❓', text: 'Food cost inconnu = gros potentiel', color: theme.amber });
    }
    
    if (diag.ebe) {
      const ebe = parseInt(diag.ebe.replace(/[^0-9-]/g, ''));
      if (ebe < 0) points.push({ icon: '🚨', text: 'EBE négatif', color: theme.red });
      else if (ebe < 2000) points.push({ icon: '💸', text: 'Marge faible', color: theme.amber });
    }
    
    // Operational issues
    if (answers.autonomie === 'pas_1_jour') {
      points.push({ icon: '⏱', text: 'Dépend du patron', color: theme.blue });
    }
    if (answers.process === 'tete') {
      points.push({ icon: '📝', text: 'Pas de process écrits', color: theme.blue });
    }
    
    // Potential
    if (diag.ca) {
      const ca = parseInt(diag.ca.replace(/[^0-9]/g, ''));
      if (ca >= 50000) points.push({ icon: '🎯', text: 'Gros CA = gros potentiel', color: theme.green });
    }
    
    if (points.length === 0) {
      points.push({ icon: '📋', text: 'Profil à analyser', color: theme.ink3 });
    }
    
    return points;
  };
  
  const strategicSummary = getStrategicSummary();
  
  const handleAddComment = () => {
    if (commentText.trim()) {
      onAddComment(lead.id, commentAuthor, commentText.trim());
      setCommentText('');
    }
  };
  
  const handleWhyWillBuyBlur = () => {
    if (whyWillBuy !== lead.why_will_buy) {
      onUpdateField(lead.id, 'why_will_buy', whyWillBuy);
    }
  };
  
  return (
    <motion.div 
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="bg-white rounded-xl border border-gray-200 overflow-hidden sticky top-24"
      data-testid="lead-detail-panel"
    >
      {/* 1. Header + Score + Call Button */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-start justify-between mb-3">
          <div>
            <button 
              onClick={onClose}
              className="lg:hidden text-xs text-gray-400 flex items-center gap-1 mb-2"
            >
              <ArrowLeft className="w-3 h-3" /> Retour
            </button>
            <h2 className="font-bold text-lg text-gray-800">{lead.restaurant}</h2>
            <p className="text-sm text-gray-500">{lead.owner}</p>
          </div>
          <div className="text-center">
            <div 
              className="text-3xl font-black"
              style={{ color: temperature.color }}
            >
              {lead.score}
            </div>
            <TemperatureBadge temperature={temperature} small />
          </div>
        </div>
        
        {/* Contact info */}
        <div className="flex flex-wrap gap-2 mb-3">
          {lead.instagram && (
            <a
              href={`https://instagram.com/${lead.instagram.replace('@', '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium hover:opacity-80"
              style={{ backgroundColor: 'rgba(225,48,108,0.1)', color: '#E1306C' }}
            >
              <Instagram className="w-3 h-3" />
              {lead.instagram}
            </a>
          )}
          {lead.city && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-blue-50 text-blue-600">
              <MapPin className="w-3 h-3" />
              {lead.city}
            </span>
          )}
        </div>
        
        {/* Quick contact actions */}
        <div className="flex gap-2 mb-3">
          {lead.phone && (
            <a
              href={`tel:${lead.phone}`}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-white text-sm font-bold active:scale-95 transition-transform"
              style={{ backgroundColor: theme.green }}
              data-testid="call-lead-btn"
            >
              <Phone className="w-4 h-4" />
              Appeler
            </a>
          )}
          {lead.phone && (
            <a
              href={`https://wa.me/${lead.phone.replace(/[^0-9]/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-white text-sm font-bold hover:opacity-90 active:scale-95 transition-all"
              style={{ backgroundColor: '#25D366' }}
              data-testid="whatsapp-lead-btn"
            >
              <MessageCircle className="w-4 h-4" />
            </a>
          )}
          {/* Bouton Instagram - toujours visible (recherche par nom) */}
          <a
            href={instagramSearchLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-white text-sm font-bold hover:opacity-90 active:scale-95 transition-all"
            style={{ background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)' }}
            data-testid="instagram-lead-btn"
          >
            <Instagram className="w-4 h-4" />
          </a>
          {/* Bouton WhatsApp Business - Ouvrir conversation */}
          <a
            href={whatsappBusinessLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-white text-sm font-bold hover:opacity-90 active:scale-95 transition-all"
            style={{ backgroundColor: '#25D366' }}
            data-testid="whatsapp-lead-btn"
          >
            <MessageCircle className="w-4 h-4" />
            <span>WhatsApp</span>
          </a>
          {/* Bouton Retargeting Email — push the 47€ book personalized */}
          <button
            onClick={async () => {
              if (!lead.audit_id) {
                alert('Pas d\'audit_id pour ce lead');
                return;
              }
              if (!confirm('Envoyer l\'email de retargeting personnalisé (vente du plan 47€) à ce lead ?')) return;
              try {
                const res = await fetch(`${API_URL}/api/admin/retarget-audit/${lead.audit_id}?force=true`, { method: 'POST' });
                const data = await res.json();
                if (data.sent) {
                  alert(`✅ Email envoyé à ${data.email}`);
                } else {
                  alert(`⚠️ Email non envoyé — raison : ${data.reason || 'inconnue'}`);
                }
              } catch (err) {
                alert('Erreur réseau : ' + err.message);
              }
            }}
            className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-white text-sm font-bold hover:opacity-90 active:scale-95 transition-all"
            style={{ backgroundColor: '#F05A1A' }}
            data-testid="retargeting-email-btn"
            title="Envoyer un email de retargeting personnalisé pour pousser le plan 47€ (3 actions perso)"
          >
            📨 Relance J+1
          </button>
          <button
            onClick={async () => {
              if (!lead.audit_id) {
                alert('Pas d\'audit_id pour ce lead');
                return;
              }
              if (!confirm('Envoyer l\'email J+7 social proof (témoignages Karim/Élodie/Yann) à ce lead ?')) return;
              try {
                const res = await fetch(`${API_URL}/api/admin/retarget-audit-j7/${lead.audit_id}?force=true`, { method: 'POST' });
                const data = await res.json();
                if (data.sent) {
                  alert(`✅ Email J+7 envoyé à ${data.email}`);
                } else {
                  alert(`⚠️ Email non envoyé — raison : ${data.reason || 'inconnue'}`);
                }
              } catch (err) {
                alert('Erreur réseau : ' + err.message);
              }
            }}
            className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-white text-sm font-bold hover:opacity-90 active:scale-95 transition-all"
            style={{ backgroundColor: '#9333ea' }}
            data-testid="retargeting-email-j7-btn"
            title="Envoyer l'email J+7 témoignages clients (sans coupon, premium)"
          >
            📨 Relance J+7
          </button>
        </div>
        
        {/* STATUS SELECTOR - Dans le panel détail */}
        <div className="mb-3">
          <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">STATUT ACTUEL</p>
          <div className="flex flex-wrap gap-1.5">
            {STATUSES.map(s => (
              <button
                key={s.id}
                onClick={() => handleStatusChangeLocal(s.id)}
                className={`px-2 py-1.5 rounded-lg text-[11px] font-bold transition-all active:scale-95 flex items-center gap-1 ${
                  currentStatus === s.id 
                    ? 'ring-2 ring-offset-1' 
                    : 'hover:opacity-80'
                }`}
                style={{ 
                  backgroundColor: `${s.color}20`,
                  color: s.color,
                  ringColor: currentStatus === s.id ? s.color : 'transparent'
                }}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* BOUTON ENREGISTRER - Visible si changements */}
        {hasChanges && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-3"
          >
            <button
              onClick={saveChanges}
              disabled={isSaving}
              className={`w-full py-3 rounded-lg text-white text-sm font-bold flex items-center justify-center gap-2 transition-all ${
                isSaving ? 'bg-gray-400' : 'bg-green-500 hover:bg-green-600 active:scale-95'
              }`}
              data-testid="save-changes-btn"
            >
              {isSaving ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  💾 Enregistrer les modifications
                </>
              )}
            </button>
          </motion.div>
        )}
        
        {/* ROW 2: Boutons action rapide */}
        <div className="flex items-center gap-2 mb-3">
          <button
            onClick={sendPaymentLink}
            className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-white text-sm font-bold bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 transition-all active:scale-95"
            data-testid="send-payment-link-btn"
          >
            💳 Envoyer paiement
          </button>
          
          {/* Bouton Diagnostic Expert */}
          <button
            onClick={fetchExpertDiagnosis}
            disabled={loadingDiagnosis}
            className={`flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm font-bold transition-all active:scale-95 ${
              showExpertPanel 
                ? 'bg-purple-600 text-white' 
                : 'bg-gradient-to-r from-purple-100 to-indigo-100 text-purple-700 hover:from-purple-200 hover:to-indigo-200'
            }`}
            data-testid="expert-diagnosis-btn"
          >
            {loadingDiagnosis ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <>🧠</>
            )}
            Expert
          </button>
          
          {/* Toggle Vue */}
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            <button
              onClick={() => setViewMode('simple')}
              className={`px-3 py-2 text-xs font-bold transition-colors ${
                viewMode === 'simple' 
                  ? 'bg-orange-500 text-white' 
                  : 'bg-white text-gray-500 hover:bg-gray-50'
              }`}
            >
              Simple
            </button>
            <button
              onClick={() => setViewMode('complete')}
              className={`px-3 py-2 text-xs font-bold transition-colors ${
                viewMode === 'complete' 
                  ? 'bg-orange-500 text-white' 
                  : 'bg-white text-gray-500 hover:bg-gray-50'
              }`}
            >
              Complet
            </button>
          </div>
        </div>
        
        {/* ROW 3: Boutons Relance J+1/J+3/J+7 */}
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[10px] text-gray-500 font-bold">🔁 Relance:</span>
          {[
            { label: 'J+1', days: 1 },
            { label: 'J+3', days: 3 },
            { label: 'J+7', days: 7 }
          ].map(r => {
            const futureDate = new Date();
            futureDate.setDate(futureDate.getDate() + r.days);
            return (
              <button
                key={r.label}
                onClick={() => {
                  onUpdateField(lead.id, 'next_followup', futureDate.toISOString().split('T')[0]);
                  onUpdateField(lead.id, 'status', 'relance');
                }}
                className="px-3 py-1.5 rounded-lg text-xs font-bold bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors active:scale-95"
              >
                {r.label}
              </button>
            );
          })}
          <button
            onClick={() => {
              onStatusChange(lead.id, 'relance');
            }}
            className="ml-auto px-3 py-1.5 rounded-lg text-xs font-bold bg-purple-100 text-purple-700 hover:bg-purple-200 transition-colors active:scale-95"
          >
            🔄 Marquer relance
          </button>
        </div>
        
        {/* ROW 4: Action recommandée */}
        <div className="p-2 rounded-lg bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200">
          <div className="flex items-center gap-2">
            <span className="text-sm">👉</span>
            <span className="text-xs font-bold text-blue-700">ACTION RECOMMANDÉE :</span>
            <span className="text-xs font-bold text-purple-700">
              {(!lead.activity?.calls && !lead.activity?.messages) ? 'Appeler maintenant' :
               lead.status === 'rdv_pris' ? 'Confirmer le RDV' :
               lead.status === 'relance' ? `Relancer (prévu ${lead.next_followup ? new Date(lead.next_followup).toLocaleDateString('fr-FR') : 'bientôt'})` :
               lead.status === 'rdv_fait' ? 'Closer le deal' :
               'Suivre le lead'}
            </span>
          </div>
        </div>
        
        {/* ROW 5: Scheduled Call Section */}
        <div className="mt-3">
          {/* If call is scheduled, show details */}
          {lead.scheduled_call ? (
            <div className="p-3 rounded-lg bg-purple-50 border-2 border-purple-300">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-purple-700 flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Appel programmé
                </span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-200 text-purple-700">
                  {lead.scheduled_call.status === 'scheduled' ? '📅 Prévu' : lead.scheduled_call.status}
                </span>
              </div>
              
              <div className="flex items-center justify-between bg-white rounded-lg p-2 mb-2">
                <div>
                  <p className="text-sm font-bold text-gray-800">
                    {new Date(lead.scheduled_call.datetime).toLocaleDateString('fr-FR', {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long'
                    })}
                  </p>
                  <p className="text-lg font-black text-purple-600">
                    {new Date(lead.scheduled_call.datetime).toLocaleTimeString('fr-FR', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
                {leadWhatsAppLink && (
                  <a
                    href={leadWhatsAppLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 rounded-lg text-sm font-bold bg-green-500 text-white hover:bg-green-600 transition-colors flex items-center gap-2"
                  >
                    <MessageCircle className="w-4 h-4" />
                    Appeler
                  </a>
                )}
              </div>
              
              {lead.scheduled_call.notes && (
                <p className="text-xs text-purple-600 italic mb-2">📝 {lead.scheduled_call.notes}</p>
              )}
              
              {/* Call outcome buttons */}
              <div className="flex gap-2">
                <button
                  onClick={() => onMarkCallOutcome && onMarkCallOutcome(lead.id, 'completed')}
                  className="flex-1 px-3 py-2 rounded-lg text-xs font-bold bg-green-100 text-green-700 hover:bg-green-200 transition-colors"
                >
                  ✅ Appel fait
                </button>
                <button
                  onClick={() => onMarkCallOutcome && onMarkCallOutcome(lead.id, 'no_show')}
                  className="flex-1 px-3 py-2 rounded-lg text-xs font-bold bg-orange-100 text-orange-700 hover:bg-orange-200 transition-colors"
                >
                  👻 No show
                </button>
                <button
                  onClick={() => onScheduleCall && onScheduleCall(lead)}
                  className="px-3 py-2 rounded-lg text-xs font-bold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                >
                  🔄 Reprog
                </button>
              </div>
            </div>
          ) : (
            /* No call scheduled - show schedule button */
            <button
              onClick={() => onScheduleCall && onScheduleCall(lead)}
              className="w-full px-4 py-3 rounded-xl text-sm font-bold bg-gradient-to-r from-purple-500 to-indigo-500 text-white hover:from-purple-600 hover:to-indigo-600 transition-all flex items-center justify-center gap-2 shadow-lg shadow-purple-500/30"
              data-testid="schedule-call-btn"
            >
              <CalendarPlus className="w-5 h-5" />
              Programmer un appel WhatsApp
            </button>
          )}
        </div>
        
        {/* Call History */}
        {lead.call_history && lead.call_history.length > 0 && (
          <div className="mt-3 p-3 rounded-lg bg-gray-50 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-gray-600 flex items-center gap-1">
                <Clock className="w-3 h-3" /> Historique des appels
              </span>
            </div>
            <CallHistory calls={lead.call_history} />
          </div>
        )}
        
        {/* ROW 6: WhatsApp Template Messages */}
        <div className="mt-3 p-3 rounded-lg bg-green-50 border border-green-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-green-700 flex items-center gap-1">
              <MessageCircle className="w-3 h-3" /> Messages WhatsApp rapides
            </span>
          </div>
          <div className="space-y-2">
            {/* Message de bienvenue */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  const welcomeMessage = `Salut 👋\nJ'ai bien reçu ton audit.\n\nJe regarde tes résultats et je reviens vers toi rapidement pour qu'on en parle ensemble 👍`;
                  navigator.clipboard.writeText(welcomeMessage);
                  alert('Message copié ! Colle-le dans WhatsApp');
                }}
                className="flex-1 px-3 py-2 rounded-lg text-xs font-medium bg-white border border-green-200 text-green-700 hover:bg-green-100 transition-colors text-left"
              >
                📋 Message de bienvenue
              </button>
              {leadWhatsAppLink && (
                <a
                  href={leadWhatsAppLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-2 rounded-lg text-xs font-bold bg-green-500 text-white hover:bg-green-600 transition-colors flex items-center gap-1"
                >
                  <MessageCircle className="w-3 h-3" /> Ouvrir
                </a>
              )}
            </div>
            {/* Message de suivi */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  const followupMessage = `Salut ${lead.owner || ''} 👋\n\nJe reviens vers toi concernant ton audit pour ${lead.restaurant || 'ton resto'}.\n\nT'as 2 min pour qu'on en parle ? Je peux t'appeler maintenant si tu veux.`;
                  navigator.clipboard.writeText(followupMessage);
                  alert('Message copié ! Colle-le dans WhatsApp');
                }}
                className="flex-1 px-3 py-2 rounded-lg text-xs font-medium bg-white border border-green-200 text-green-700 hover:bg-green-100 transition-colors text-left"
              >
                📋 Message de relance
              </button>
            </div>
          </div>
        </div>
        
        {/* EXPERT DIAGNOSIS PANEL */}
        <AnimatePresence>
          {showExpertPanel && expertDiagnosis && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-3"
            >
              <div className="rounded-xl border-2 border-purple-200 overflow-hidden bg-gradient-to-b from-purple-50 to-white">
                {/* Header */}
                <div className="p-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">🧠</span>
                      <span className="font-bold">DIAGNOSTIC EXPERT</span>
                    </div>
                    <button 
                      onClick={() => setShowExpertPanel(false)}
                      className="p-1 hover:bg-white/20 rounded transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                {/* Score + Pertes */}
                <div className="p-3 border-b border-purple-100">
                  <div className="flex items-center justify-between gap-4">
                    <div className="text-center">
                      <div className={`text-3xl font-black ${
                        expertDiagnosis.score?.score >= 60 ? 'text-green-500' :
                        expertDiagnosis.score?.score >= 40 ? 'text-amber-500' : 'text-red-500'
                      }`}>
                        {expertDiagnosis.score?.score || 0}/100
                      </div>
                      <p className="text-[10px] text-gray-500 uppercase font-bold">Score Resto</p>
                    </div>
                    <div className="text-center px-4 py-2 rounded-lg bg-red-50 border border-red-200">
                      <div className="text-2xl font-black text-red-600">
                        -{expertDiagnosis.pertes?.total?.toLocaleString() || 0}€
                      </div>
                      <p className="text-[10px] text-red-500 uppercase font-bold">Pertes/mois</p>
                    </div>
                    <div className="text-center px-4 py-2 rounded-lg bg-green-50 border border-green-200">
                      <div className="text-2xl font-black text-green-600">
                        +{expertDiagnosis.pertes?.gain_potentiel_90j?.toLocaleString() || 0}€
                      </div>
                      <p className="text-[10px] text-green-500 uppercase font-bold">Gain 90 jours</p>
                    </div>
                  </div>
                </div>
                
                {/* Top 3 Actions */}
                <div className="p-3 border-b border-purple-100">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-purple-600 mb-2">
                    🎯 PLAN D'ACTION PRIORITAIRE
                  </p>
                  <div className="space-y-2">
                    {expertDiagnosis.top_3_actions?.map((action, i) => (
                      <div 
                        key={i}
                        className="flex items-start gap-2 p-2 rounded-lg bg-white border border-gray-100"
                      >
                        <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                          i === 0 ? 'bg-red-500' : i === 1 ? 'bg-amber-500' : 'bg-blue-500'
                        }`}>
                          {i + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-gray-800">{action.action}</p>
                          <p className="text-xs text-gray-500">{action.impact}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-[10px] font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded">
                              💰 {action.gain}
                            </span>
                            {action.tools?.map((tool, j) => (
                              <span key={j} className="text-[9px] font-bold text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">
                                {tool}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Outils Recommandés */}
                {expertDiagnosis.tools_recommandes?.length > 0 && (
                  <div className="p-3 border-b border-purple-100">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-purple-600 mb-2">
                      🛠️ OUTILS RECOMMANDÉS
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {expertDiagnosis.tools_recommandes?.slice(0, 4).map((tool, i) => (
                        <div 
                          key={i}
                          className="p-2 rounded-lg bg-white border border-gray-100 hover:border-purple-300 transition-colors cursor-pointer"
                          onClick={() => window.open(tool.url, '_blank')}
                        >
                          <p className="text-xs font-bold text-gray-800">{tool.name}</p>
                          <p className="text-[10px] text-gray-500 line-clamp-1">{tool.description}</p>
                          <p className="text-[9px] text-green-600 font-medium mt-1">{tool.impact}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Projection 12 mois */}
                <div className="p-3 bg-gradient-to-r from-green-50 to-emerald-50">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-green-600 mb-2">
                    📈 PROJECTION 12 MOIS
                  </p>
                  <div className="flex items-center justify-center gap-4">
                    <div className="text-center">
                      <div className="text-xl font-black text-green-600">
                        +{(expertDiagnosis.projection_12_mois?.gain_potentiel || 0).toLocaleString()}€
                      </div>
                      <p className="text-[10px] text-gray-500">Sans accompagnement</p>
                    </div>
                    <span className="text-2xl">→</span>
                    <div className="text-center px-4 py-2 rounded-lg bg-gradient-to-r from-orange-500 to-amber-500 text-white">
                      <div className="text-xl font-black">
                        +{(expertDiagnosis.projection_12_mois?.avec_prsnl || 0).toLocaleString()}€
                      </div>
                      <p className="text-[10px] opacity-90">Avec PRSNL</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* 2. Strategic Summary (IMPORTANT) */}
      <div className="p-4 border-b border-gray-100" style={{ backgroundColor: 'rgba(232,98,26,0.03)' }}>
        <p className="text-[10px] uppercase tracking-wider font-bold mb-2" style={{ color: theme.orange }}>
          🔥 Résumé du lead
        </p>
        <div className="space-y-1.5">
          {strategicSummary.map((point, i) => (
            <div 
              key={i}
              className="flex items-center gap-2 text-sm font-medium"
              style={{ color: point.color }}
            >
              <span>{point.icon}</span>
              <span>{point.text}</span>
            </div>
          ))}
        </div>
        
        {/* Estimated loss */}
        {estimatedLoss && (
          <div className="mt-3 p-2 rounded-lg" style={{ backgroundColor: 'rgba(220,38,38,0.1)' }}>
            <p className="text-[10px] uppercase tracking-wider font-bold text-red-600 mb-1">💸 Perte estimée</p>
            <p className="text-lg font-black text-red-600">{estimatedLoss.toLocaleString('fr-FR')}€ / mois</p>
          </div>
        )}
      </div>

      {/* 3. "Why will they buy" field */}
      <div className="p-4 border-b border-gray-100">
        <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">🧠 Pourquoi il va acheter (Setter)</p>
        <textarea
          value={whyWillBuy}
          onChange={(e) => setWhyWillBuy(e.target.value)}
          onBlur={handleWhyWillBuyBlur}
          placeholder="Ex: Fatigué, veut déléguer, perd de l'argent..."
          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-orange-400 resize-none"
          rows={2}
          data-testid="why-will-buy-input"
        />
      </div>

      {/* 4. Diagnostic */}
      {lead.diag && Object.keys(lead.diag).length > 0 && (
        <div className="p-4 border-b border-gray-100">
          <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">📊 Diagnostic</p>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(lead.diag).map(([key, value]) => {
              const labels = {
                ca: 'CA mensuel', fc: 'Food Cost', ms: 'Masse Sal.', mn: 'Marge Nette',
                loyer: 'Loyer', ebe: 'EBE', prime_cost: 'Prime Cost'
              };
              const isWarning = (key === 'fc' && value !== 'Inconnu' && parseInt(value) > 30) ||
                               (key === 'prime_cost' && value !== 'Inconnu' && parseInt(value) > 60) ||
                               (key === 'ebe' && parseInt(value.replace(/[^0-9-]/g, '')) < 2000);
              
              return (
                <div 
                  key={key} 
                  className="p-2 rounded-lg"
                  style={{ backgroundColor: isWarning ? 'rgba(239,68,68,0.05)' : theme.bg }}
                >
                  <p className="text-[10px] text-gray-500 uppercase">{labels[key] || key}</p>
                  <p className={`font-bold ${isWarning ? 'text-red-600' : 'text-gray-800'}`}>{value || '—'}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* 4.5 ANALYSE DES VENTES - Tableau récapitulatif */}
      <div className="p-4 border-b border-gray-100">
        <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">💰 Analyse des Ventes</p>
        <SalesAnalysisTable lead={lead} onUpdateField={onUpdateField} />
      </div>
      
      {/* 🔶 PRSNL ACCOMPAGNEMENT - REMONTÉ ICI (juste après analyse ventes) */}
      <div className="p-4 border-b border-gray-100">
        <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">🔶 Offre PRSNL (Closing)</p>
        <PRSNLAccompagnementSection lead={lead} />
      </div>
      
      {/* 📊 KPI LEAD + SCORE CLOSING AUTO - Compact */}
      <div className="p-4 border-b border-gray-100">
        <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">📊 KPI LEAD</p>
        <div className="grid grid-cols-4 gap-2 mb-3">
          <div className="p-2 rounded-lg bg-gray-50 text-center">
            <p className="text-xl font-black text-gray-800">{lead.activity?.calls || 0}</p>
            <p className="text-[9px] text-gray-500">Appels</p>
          </div>
          <div className="p-2 rounded-lg bg-gray-50 text-center">
            <p className="text-xl font-black text-green-600">{lead.activity?.pickups || 0}</p>
            <p className="text-[9px] text-gray-500">Décrochés</p>
          </div>
          <div className="p-2 rounded-lg bg-gray-50 text-center">
            <p className="text-xl font-black text-blue-600">{lead.activity?.messages || 0}</p>
            <p className="text-[9px] text-gray-500">Messages</p>
          </div>
          <div className="p-2 rounded-lg bg-gray-50 text-center">
            <p className="text-sm font-bold text-gray-700">
              {lead.activity?.calls > 0 ? `${Math.round((lead.activity?.pickups || 0) / lead.activity.calls * 100)}%` : '—'}
            </p>
            <p className="text-[9px] text-gray-500">Taux</p>
          </div>
        </div>
        {/* Score Closing AUTO-CALCULÉ */}
        <div className="p-3 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-500">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-bold text-white/80">🎯 SCORE CLOSING (auto)</span>
            <span className="text-2xl font-black text-white">{autoClosingScore}%</span>
          </div>
          <div className="w-full h-2 bg-white/20 rounded-full overflow-hidden">
            <div 
              className="h-full bg-white rounded-full transition-all duration-500"
              style={{ width: `${autoClosingScore}%` }}
            />
          </div>
          <p className="text-[10px] text-white/60 mt-1">
            Basé sur : activité, statut, qualité lead, psychologie
          </p>
        </div>
      </div>

      {/* === SECTIONS EN MODE COMPLET UNIQUEMENT === */}
      {viewMode === 'complete' && (
        <>
          {/* Timeline Section */}
          <div className="p-4 border-b border-gray-100">
            <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">📅 Timeline</p>
            <div className="grid grid-cols-3 gap-2">
              <div className="p-2 rounded-lg bg-blue-50">
                <p className="text-[10px] text-blue-600 uppercase">Entrée</p>
                <p className="font-bold text-blue-700 text-sm">{formatDate(lead.created_at)}</p>
              </div>
              <div className="p-2 rounded-lg bg-amber-50">
                <p className="text-[10px] text-amber-600 uppercase">Dernier contact</p>
                <p className="font-bold text-amber-700 text-sm">{lead.last_action_at ? timeAgo(lead.last_action_at) : '—'}</p>
              </div>
              <div className="p-2 rounded-lg bg-green-50">
                <p className="text-[10px] text-green-600 uppercase">Prochaine relance</p>
                <p className="font-bold text-green-700 text-sm">{lead.next_followup ? formatDate(lead.next_followup) : '—'}</p>
              </div>
            </div>
            {/* Next followup date picker */}
            <div className="mt-2">
              <input
                type="date"
                value={lead.next_followup || ''}
                onChange={(e) => onUpdateField(lead.id, 'next_followup', e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-green-400"
                placeholder="Planifier prochaine relance"
              />
            </div>
          </div>

          {/* Activity Section */}
      <div className="p-4 border-b border-gray-100">
        <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">📞 Activité</p>
        <div className="grid grid-cols-3 gap-2">
          <div className="p-2 rounded-lg text-center" style={{ backgroundColor: theme.bg }}>
            <p className="text-2xl font-black text-gray-800">{lead.activity?.calls || 0}</p>
            <p className="text-[10px] text-gray-500">Appels</p>
          </div>
          <div className="p-2 rounded-lg text-center" style={{ backgroundColor: theme.bg }}>
            <p className="text-2xl font-black text-green-600">{lead.activity?.pickups || 0}</p>
            <p className="text-[10px] text-gray-500">Décrochés</p>
          </div>
          <div className="p-2 rounded-lg text-center" style={{ backgroundColor: theme.bg }}>
            <p className="text-2xl font-black text-blue-600">{lead.activity?.messages || 0}</p>
            <p className="text-[10px] text-gray-500">Messages</p>
          </div>
        </div>
        {/* Quick log buttons */}
        <div className="flex gap-2 mt-2">
          <button
            onClick={() => {
              const activity = lead.activity || { calls: 0, pickups: 0, messages: 0 };
              onUpdateField(lead.id, 'activity', { ...activity, calls: (activity.calls || 0) + 1 });
            }}
            className="flex-1 py-2 rounded-lg text-xs font-bold bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            +1 Appel
          </button>
          <button
            onClick={() => {
              const activity = lead.activity || { calls: 0, pickups: 0, messages: 0 };
              onUpdateField(lead.id, 'activity', { ...activity, pickups: (activity.pickups || 0) + 1 });
            }}
            className="flex-1 py-2 rounded-lg text-xs font-bold bg-green-100 text-green-700 hover:bg-green-200 transition-colors"
          >
            +1 Décroché
          </button>
          <button
            onClick={() => {
              const activity = lead.activity || { calls: 0, pickups: 0, messages: 0 };
              onUpdateField(lead.id, 'activity', { ...activity, messages: (activity.messages || 0) + 1 });
            }}
            className="flex-1 py-2 rounded-lg text-xs font-bold bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors"
          >
            +1 Msg
          </button>
        </div>
      </div>

      {/* Relance Section */}
      <div className="p-4 border-b border-gray-100">
        <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">🔁 Relance</p>
        <div className="grid grid-cols-2 gap-2 mb-2">
          <div className="p-2 rounded-lg" style={{ backgroundColor: theme.bg }}>
            <p className="text-[10px] text-gray-500">Nb relances</p>
            <p className="font-bold text-gray-800">{lead.followup?.count || 0}</p>
          </div>
          <div className="p-2 rounded-lg" style={{ backgroundColor: theme.bg }}>
            <p className="text-[10px] text-gray-500">Dernier canal</p>
            <p className="font-bold text-gray-800">{lead.followup?.last_channel || '—'}</p>
          </div>
        </div>
        {/* Followup status */}
        <div className="flex gap-2">
          <button
            onClick={() => onUpdateField(lead.id, 'followup', { ...lead.followup, status: 'a_faire', count: (lead.followup?.count || 0) })}
            className={`flex-1 py-2 rounded-lg text-xs font-bold transition-colors ${
              lead.followup?.status === 'a_faire' ? 'bg-amber-500 text-white' : 'bg-amber-100 text-amber-700'
            }`}
          >
            ⏳ À faire
          </button>
          <button
            onClick={() => onUpdateField(lead.id, 'followup', { ...lead.followup, status: 'en_cours', count: (lead.followup?.count || 0) })}
            className={`flex-1 py-2 rounded-lg text-xs font-bold transition-colors ${
              lead.followup?.status === 'en_cours' ? 'bg-blue-500 text-white' : 'bg-blue-100 text-blue-700'
            }`}
          >
            🔁 En cours
          </button>
          <button
            onClick={() => onUpdateField(lead.id, 'followup', { ...lead.followup, status: 'fait', count: (lead.followup?.count || 0) + 1, last_channel: 'Appel' })}
            className={`flex-1 py-2 rounded-lg text-xs font-bold transition-colors ${
              lead.followup?.status === 'fait' ? 'bg-green-500 text-white' : 'bg-green-100 text-green-700'
            }`}
          >
            ✅ Fait
          </button>
        </div>
      </div>

      {/* Status selector */}
      <div className="p-4 border-b border-gray-100">
        <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">Statut</p>
        <div className="flex flex-wrap gap-2">
          {STATUSES.map(s => (
            <StatusBadge
              key={s.id}
              status={s.id}
              active={lead.status === s.id}
              onClick={() => onStatusChange(lead.id, s.id)}
            />
          ))}
        </div>
      </div>

      {/* 5. Setter info */}
      <div className="p-4 border-b border-gray-100">
        <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">📞 Setter</p>
        
        {/* Show current setter contact info */}
        {currentSetter && (
          <div className="mb-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                style={{ backgroundColor: currentSetter.avatar_color || '#B8760A' }}
              >
                {currentSetter.name.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1">
                <p className="font-bold text-amber-800">{currentSetter.name}</p>
                <div className="flex flex-wrap gap-2 mt-1">
                  {currentSetter.phone && (
                    <a href={`tel:${currentSetter.phone}`} className="text-xs text-amber-600 hover:underline flex items-center gap-1">
                      <Phone className="w-3 h-3" /> {currentSetter.phone}
                    </a>
                  )}
                  {currentSetter.email && (
                    <a href={`mailto:${currentSetter.email}`} className="text-xs text-amber-600 hover:underline flex items-center gap-1">
                      <Mail className="w-3 h-3" /> Email
                    </a>
                  )}
                </div>
              </div>
              {currentSetter.phone && (
                <a
                  href={`https://wa.me/${currentSetter.phone.replace(/[^0-9]/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1.5 rounded-lg text-white text-xs font-bold"
                  style={{ backgroundColor: '#25D366' }}
                >
                  💬
                </a>
              )}
            </div>
          </div>
        )}
        
        <div className="space-y-2">
          <select
            value={lead.setter?.name || ''}
            onChange={(e) => onWorkflowUpdate('setter', 'name', e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400 bg-white"
          >
            <option value="">Sélectionner un setter...</option>
            {setters.length > 0 ? (
              setters.map(m => (
                <option key={m.id} value={m.name}>{m.name}</option>
              ))
            ) : (
              <>
                <option value="Mathis">Mathis</option>
                <option value="Lucas">Lucas</option>
                <option value="Emma">Emma</option>
              </>
            )}
          </select>
          <input
            type="text"
            value={lead.setter?.note || ''}
            onChange={(e) => onWorkflowUpdate('setter', 'note', e.target.value)}
            placeholder="Note setter..."
            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400"
          />
        </div>
      </div>

      {/* 6. Closer info */}
      <div className="p-4 border-b border-gray-100">
        <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">📅 Closer</p>
        
        {/* Show current closer contact info */}
        {currentCloser && (
          <div className="mb-3 p-3 rounded-lg bg-blue-50 border border-blue-200">
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                style={{ backgroundColor: currentCloser.avatar_color || '#1A4A8A' }}
              >
                {currentCloser.name.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1">
                <p className="font-bold text-blue-800">{currentCloser.name}</p>
                <div className="flex flex-wrap gap-2 mt-1">
                  {currentCloser.phone && (
                    <a href={`tel:${currentCloser.phone}`} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                      <Phone className="w-3 h-3" /> {currentCloser.phone}
                    </a>
                  )}
                  {currentCloser.email && (
                    <a href={`mailto:${currentCloser.email}`} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                      <Mail className="w-3 h-3" /> Email
                    </a>
                  )}
                </div>
              </div>
              {currentCloser.phone && (
                <a
                  href={`https://wa.me/${currentCloser.phone.replace(/[^0-9]/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1.5 rounded-lg text-white text-xs font-bold"
                  style={{ backgroundColor: '#25D366' }}
                >
                  💬
                </a>
              )}
            </div>
          </div>
        )}
        
        {/* Calendly RDV Info - Keep for historical data but update text */}
        {lead.calendly_event && (
          <div className="mb-3 p-3 rounded-lg bg-green-50 border border-green-200">
            <div className="flex items-center gap-2 mb-1">
              <Phone className="w-4 h-4 text-green-600" />
              <span className="text-sm font-bold text-green-700">Appel Programmé</span>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                lead.calendly_event.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
              }`}>
                {lead.calendly_event.status === 'active' ? '✅ Confirmé' : lead.calendly_event.status}
              </span>
            </div>
            <p className="text-sm text-green-800 font-medium">
              {new Date(lead.calendly_event.start_time).toLocaleDateString('fr-FR', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
            {/* WhatsApp link instead of visio */}
            {leadWhatsAppLink && (
              <a
                href={leadWhatsAppLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 mt-2 text-xs text-green-600 hover:underline"
              >
                📱 Contacter via WhatsApp
              </a>
            )}
          </div>
        )}
        
        <div className="space-y-2">
          <select
            value={lead.closer?.name || ''}
            onChange={(e) => onWorkflowUpdate('closer', 'name', e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-blue-400 bg-white"
          >
            <option value="">Sélectionner un closer...</option>
            {closers.length > 0 ? (
              closers.map(m => (
                <option key={m.id} value={m.name}>{m.name}</option>
              ))
            ) : (
              <>
                <option value="Massimo">Massimo</option>
                <option value="Sabril">Sabril</option>
                <option value="Thomas">Thomas</option>
              </>
            )}
          </select>
          <input
            type="datetime-local"
            value={lead.closer?.rdv_at || ''}
            onChange={(e) => onWorkflowUpdate('closer', 'rdv_at', e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-blue-400"
          />
          {/* WhatsApp button with template message */}
          <a
            href={leadWhatsAppLink || whatsappBusinessLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full py-2.5 rounded-lg text-white text-sm font-bold transition-all hover:opacity-90"
            style={{ backgroundColor: '#25D366' }}
            data-testid="whatsapp-closer-btn"
          >
            <MessageCircle className="w-4 h-4" />
            Appel WhatsApp (15 min)
          </a>
        </div>
      </div>

      {/* 7. Comments / Notes */}
      <div className="p-4 border-b border-gray-100">
        <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">💬 Notes</p>
        
        <div className="space-y-2 max-h-40 overflow-y-auto mb-3">
          {(!lead.comments || lead.comments.length === 0) ? (
            <p className="text-xs text-gray-400 italic text-center py-3">Aucune note</p>
          ) : (
            [...lead.comments].reverse().map(c => (
              <div 
                key={c.id} 
                className="p-2 rounded-lg text-xs"
                style={{ 
                  backgroundColor: c.author === 'closer' ? 'rgba(26,74,138,0.1)' : 'rgba(184,118,10,0.1)',
                  borderLeft: `3px solid ${c.author === 'closer' ? theme.blue : theme.amber}`
                }}
              >
                <div className="flex justify-between items-start mb-1">
                  <span className="font-bold capitalize" style={{ color: c.author === 'closer' ? theme.blue : theme.amber }}>
                    {c.author}
                  </span>
                  <span className="text-[10px] text-gray-400">{timeAgo(c.created_at)}</span>
                </div>
                <p className="text-gray-700">{c.text}</p>
              </div>
            ))
          )}
        </div>
        
        <div className="flex gap-2 mb-2">
          <button
            onClick={() => setCommentAuthor('setter')}
            className={`text-[10px] px-2 py-1 rounded ${commentAuthor === 'setter' ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-600'}`}
          >
            Setter
          </button>
          <button
            onClick={() => setCommentAuthor('closer')}
            className={`text-[10px] px-2 py-1 rounded ${commentAuthor === 'closer' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600'}`}
          >
            Closer
          </button>
        </div>
        
        <div className="flex gap-2">
          <input
            type="text"
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Ajouter une note..."
            className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none"
            onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
          />
          <button
            onClick={handleAddComment}
            disabled={!commentText.trim()}
            className="px-4 py-2 rounded-lg text-white text-sm font-bold disabled:opacity-50"
            style={{ backgroundColor: theme.orange }}
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* 8. Last action indicator */}
      <div className="p-4 border-b border-gray-100 bg-gray-50">
        <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">🕒 Dernière action</p>
        <p className="text-sm font-medium text-gray-700">
          {lead.last_action ? (
            <>{lead.last_action} — {timeAgo(lead.last_action_at)}</>
          ) : lead.setter?.contacted_at ? (
            <>Appelé — {timeAgo(lead.setter.contacted_at)}</>
          ) : (
            <>Créé — {timeAgo(lead.created_at)}</>
          )}
        </p>
      </div>
        </>
      )}
      
      {/* === FIN MODE COMPLET === */}

      {/* Footer */}
      <div className="p-4 flex flex-col gap-3">
        <button
          onClick={() => onDelete(lead.id)}
          className="w-full py-2.5 rounded-lg text-sm font-bold text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 flex items-center justify-center gap-2 transition-colors"
          data-testid="delete-lead-btn"
        >
          <Trash2 className="w-4 h-4" />
          Supprimer cette fiche
        </button>
        <span className="text-[10px] text-gray-400 text-center">
          Créé le {formatDate(lead.created_at)}
        </span>
      </div>
    </motion.div>
  );
};

// Main CRM Component
const CRMPage = () => {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState({});
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false); // Toggle notifications panel
  
  // AI PREDICTIONS STATE
  const [aiPredictions, setAiPredictions] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [aiLastUpdated, setAiLastUpdated] = useState(null);
  
  // NOUVEAUX FILTRES
  const [dateFilter, setDateFilter] = useState('all'); // 'all', 'today', 'yesterday', '7days', '30days'
  const [sortBy, setSortBy] = useState('priority'); // 'priority', 'date', 'score', 'potential'
  const [callFocusMode, setCallFocusMode] = useState(false); // Mode appel = que les leads à appeler

  // CALENDAR & SCHEDULING STATE
  const [showCalendar, setShowCalendar] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleModalLead, setScheduleModalLead] = useState(null);
  const [crmNotifications, setCrmNotifications] = useState([]);

  // Email preferences state
  const [emailPrefs, setEmailPrefs] = useState({
    send_stage_emails: true,
    send_relance_j1: true,
    send_relance_j3: true,
    send_relance_j7: true,
    send_hot_lead_alerts: true,
    hot_lead_threshold: 80
  });
  const [showEmailSettings, setShowEmailSettings] = useState(false);

  // Check if unlocked (also validates auth version — invalidates all old sessions after code rotation)
  useEffect(() => {
    const unlocked = localStorage.getItem('crm_unlocked');
    const version = localStorage.getItem('crm_auth_version');
    if (unlocked === 'true' && version === CRM_AUTH_VERSION) {
      setIsUnlocked(true);
    } else if (unlocked === 'true' && version !== CRM_AUTH_VERSION) {
      // Stale session — purge and force re-auth with new code
      localStorage.removeItem('crm_unlocked');
      localStorage.removeItem('crm_member');
      localStorage.removeItem('crm_auth_version');
    }
  }, []);

  // Fetch leads
  const fetchLeads = async () => {
    try {
      const res = await fetch(`${API_URL}/api/crm/leads`);
      const data = await res.json();
      setLeads(data);
    } catch (err) {
      console.error('Failed to fetch leads:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/crm/stats`);
      const data = await res.json();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  // Fetch team members
  const fetchTeamMembers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/team/members`);
      const data = await res.json();
      setTeamMembers(data);
    } catch (err) {
      console.error('Failed to fetch team members:', err);
    }
  };

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      const res = await fetch(`${API_URL}/api/notifications?unread_only=true`);
      const data = await res.json();
      setNotifications(data);
      setNotificationCount(data.length);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  };
  
  // Mark notification as read
  const markNotificationRead = async (notificationId) => {
    try {
      await fetch(`${API_URL}/api/notifications/${notificationId}/read`, {
        method: 'PUT'
      });
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      setNotificationCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark notification read:', err);
    }
  };
  
  // Mark all notifications as read
  const markAllNotificationsRead = async () => {
    try {
      await fetch(`${API_URL}/api/notifications/mark-all-read`, {
        method: 'PUT'
      });
      setNotifications([]);
      setNotificationCount(0);
    } catch (err) {
      console.error('Failed to mark all notifications read:', err);
    }
  };
  
  // Fetch AI Predictions
  const fetchAiPredictions = async () => {
    setAiLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/crm/ai-scoring`);
      const data = await res.json();
      if (data.predictions) {
        setAiPredictions(data.predictions);
        setAiLastUpdated(data.analyzed_at);
      }
    } catch (err) {
      console.error('Failed to fetch AI predictions:', err);
    } finally {
      setAiLoading(false);
    }
  };

  // Fetch email preferences
  const fetchEmailPrefs = async () => {
    try {
      const res = await fetch(`${API_URL}/api/settings/email-preferences`);
      const data = await res.json();
      if (data) {
        setEmailPrefs(data);
      }
    } catch (err) {
      console.error('Failed to fetch email preferences:', err);
    }
  };

  // Update email preferences
  const updateEmailPrefs = async (newPrefs) => {
    try {
      const res = await fetch(`${API_URL}/api/settings/email-preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newPrefs)
      });
      const data = await res.json();
      if (data.status === 'success') {
        setEmailPrefs(newPrefs);
      }
    } catch (err) {
      console.error('Failed to update email preferences:', err);
    }
  };

  // Trigger manual relance check
  const triggerRelanceCheck = async () => {
    try {
      const res = await fetch(`${API_URL}/api/crm/check-relances`, { method: 'POST' });
      const data = await res.json();
      alert(data.message || 'Relance check completed!');
    } catch (err) {
      console.error('Failed to trigger relance check:', err);
    }
  };

  // Push notification state
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  
  // Initialize push notifications
  const initPushNotifications = useCallback(async () => {
    const initialized = await pushService.init();
    if (initialized) {
      setPushPermission(pushService.getPermissionStatus());
      setPushEnabled(pushService.isSubscribed());
    }
  }, []);
  
  // Toggle push notifications
  const togglePushNotifications = async () => {
    if (pushEnabled) {
      const result = await pushService.unsubscribe();
      if (result.success) {
        setPushEnabled(false);
      }
    } else {
      const permission = await pushService.requestPermission();
      setPushPermission(permission);
      
      if (permission === 'granted') {
        // Get current user info from localStorage if available
        const savedMember = localStorage.getItem('crm_member');
        const memberInfo = savedMember ? JSON.parse(savedMember) : {};
        
        const result = await pushService.subscribe(memberInfo);
        if (result.success) {
          setPushEnabled(true);
        }
      }
    }
  };

  useEffect(() => {
    if (isUnlocked) {
      fetchLeads();
      fetchStats();
      fetchTeamMembers();
      fetchNotifications();
      fetchEmailPrefs();
      initPushNotifications();
      const interval = setInterval(() => {
        fetchLeads();
        fetchStats();
        fetchNotifications();
      }, 15000); // Poll every 15 seconds
      return () => clearInterval(interval);
    }
  }, [isUnlocked, initPushNotifications]);

  // Filter leads
  const filteredLeads = useMemo(() => {
    let result = leads;
    
    // Mode Call Focus : que les leads à appeler
    if (callFocusMode) {
      result = result.filter(l => 
        l.status === 'nouveau' || 
        l.status === 'a_contacter' || 
        l.status === 'tentative' ||
        l.status === 'relance' ||
        (!l.activity?.calls && !l.activity?.messages)
      );
    }
    
    // Filtre par statut
    if (filter !== 'all') {
      result = result.filter(l => l.status === filter);
    }
    
    // Filtre par date
    if (dateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);
      const monthAgo = new Date(today);
      monthAgo.setDate(monthAgo.getDate() - 30);
      
      result = result.filter(l => {
        const createdAt = new Date(l.created_at);
        switch(dateFilter) {
          case 'today':
            return createdAt >= today;
          case 'yesterday':
            return createdAt >= yesterday && createdAt < today;
          case '7days':
            return createdAt >= weekAgo;
          case '30days':
            return createdAt >= monthAgo;
          default:
            return true;
        }
      });
    }
    
    // Recherche texte
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(l => 
        l.restaurant?.toLowerCase().includes(q) ||
        l.owner?.toLowerCase().includes(q) ||
        l.city?.toLowerCase().includes(q) ||
        l.phone?.includes(q)
      );
    }
    
    // TRI
    result.sort((a, b) => {
      switch(sortBy) {
        case 'date':
          // Plus récent en premier
          return new Date(b.created_at) - new Date(a.created_at);
          
        case 'score':
          // Score le plus bas en premier (= plus de problèmes)
          return (a.score || 100) - (b.score || 100);
          
        case 'potential':
          // Potentiel € le plus élevé en premier
          const aCa = parseInt(String(a.diag?.ca || '0').replace(/[^0-9]/g, '')) || 0;
          const bCa = parseInt(String(b.diag?.ca || '0').replace(/[^0-9]/g, '')) || 0;
          return bCa - aCa;
          
        case 'priority':
        default:
          // 1. Leads non traités en premier (0 appels, 0 messages)
          const aUntreated = (!a.activity?.calls && !a.activity?.messages) ? 1 : 0;
          const bUntreated = (!b.activity?.calls && !b.activity?.messages) ? 1 : 0;
          if (aUntreated !== bUntreated) return bUntreated - aUntreated;
          
          // 2. Leads NEW en priorité
          if (a.is_new !== b.is_new) return a.is_new ? -1 : 1;
          
          // 3. Score bas = plus de problèmes = priorité
          const aScore = a.score || 100;
          const bScore = b.score || 100;
          if (Math.abs(aScore - bScore) > 20) return aScore - bScore;
          
          // 4. RDV pris
          const aRdv = a.status === 'rdv_pris' ? 1 : 0;
          const bRdv = b.status === 'rdv_pris' ? 1 : 0;
          if (aRdv !== bRdv) return bRdv - aRdv;
          
          // 5. Forte valeur (CA élevé = potentiel élevé)
          const aCaVal = parseInt(String(a.diag?.ca || '0').replace(/[^0-9]/g, '')) || 0;
          const bCaVal = parseInt(String(b.diag?.ca || '0').replace(/[^0-9]/g, '')) || 0;
          if (aCaVal !== bCaVal) return bCaVal - aCaVal;
          
          // 6. Dernière action (plus ancienne = plus urgent)
          const aTime = a.last_action_at ? new Date(a.last_action_at).getTime() : 0;
          const bTime = b.last_action_at ? new Date(b.last_action_at).getTime() : 0;
          return aTime - bTime;
      }
    });
    
    return result;
  }, [leads, filter, searchQuery, dateFilter, sortBy, callFocusMode]);

  // Count by status + stats today
  const statusCounts = useMemo(() => {
    const counts = {};
    STATUSES.forEach(s => {
      counts[s.id] = leads.filter(l => l.status === s.id).length;
    });
    counts.all = leads.length;
    counts.new = leads.filter(l => l.is_new).length;
    
    // Stats aujourd'hui
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    counts.today = leads.filter(l => new Date(l.created_at) >= today).length;
    counts.rdv_today = leads.filter(l => 
      l.status === 'rdv_pris' && l.calendly_event?.start_time && 
      new Date(l.calendly_event.start_time) >= today
    ).length;
    
    // Taux de contact
    const contactedLeads = leads.filter(l => l.activity?.calls > 0 || l.activity?.messages > 0);
    counts.contact_rate = leads.length > 0 ? Math.round(contactedLeads.length / leads.length * 100) : 0;
    
    // Taux de closing
    const closedLeads = leads.filter(l => l.status === 'gagne');
    counts.closing_rate = leads.length > 0 ? Math.round(closedLeads.length / leads.length * 100) : 0;
    
    // Leads à traiter (non contactés)
    counts.untreated = leads.filter(l => !l.activity?.calls && !l.activity?.messages).length;
    
    return counts;
  }, [leads]);

  // Select lead
  const selectLead = async (lead) => {
    setSelectedLead(lead);
    
    if (lead.is_new) {
      try {
        await fetch(`${API_URL}/api/crm/leads/${lead.id}/mark-seen`, { method: 'PUT' });
        setLeads(prev => prev.map(l => l.id === lead.id ? { ...l, is_new: false } : l));
        fetchStats();
      } catch (err) {
        console.error('Failed to mark lead as seen:', err);
      }
    }
  };

  // Update lead status
  const updateStatus = async (leadId, status) => {
    try {
      const res = await fetch(`${API_URL}/api/crm/leads/${leadId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          status,
          last_action: `Statut → ${getStatus(status).label}`,
          last_action_at: new Date().toISOString()
        })
      });
      const data = await res.json();
      if (data.lead) {
        setLeads(prev => prev.map(l => l.id === data.lead.id ? data.lead : l));
        if (selectedLead?.id === leadId) {
          setSelectedLead(data.lead);
        }
        fetchStats();
      }
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  // Update workflow
  const updateWorkflow = async (type, field, value) => {
    if (!selectedLead) return;
    const updateData = {
      [type]: {
        ...selectedLead[type],
        [field]: value
      },
      last_action: `${type === 'setter' ? 'Setter' : 'Closer'}: ${field}`,
      last_action_at: new Date().toISOString()
    };
    
    try {
      const res = await fetch(`${API_URL}/api/crm/leads/${selectedLead.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });
      const data = await res.json();
      if (data.lead) {
        setSelectedLead(data.lead);
        setLeads(prev => prev.map(l => l.id === data.lead.id ? data.lead : l));
      }
    } catch (err) {
      console.error('Failed to update workflow:', err);
    }
  };

  // Update single field
  const updateField = async (leadId, field, value) => {
    try {
      const res = await fetch(`${API_URL}/api/crm/leads/${leadId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          [field]: value,
          last_action: `Modifié: ${field}`,
          last_action_at: new Date().toISOString()
        })
      });
      const data = await res.json();
      if (data.lead) {
        setSelectedLead(data.lead);
        setLeads(prev => prev.map(l => l.id === data.lead.id ? data.lead : l));
      }
    } catch (err) {
      console.error('Failed to update field:', err);
    }
  };

  // Schedule a call with a lead
  const scheduleCall = async (leadId, datetime, notes) => {
    try {
      const res = await fetch(`${API_URL}/api/crm/leads/${leadId}/schedule-call`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ datetime, notes })
      });
      const data = await res.json();
      if (data.status === 'success') {
        // Add CRM notification
        const lead = leads.find(l => l.id === leadId);
        if (lead) {
          const notification = getScheduledCallNotification(lead, datetime);
          setCrmNotifications(prev => [notification, ...prev]);
          
          // Copy WhatsApp reminder to clipboard
          const reminderText = getWhatsAppReminderTemplate(lead, datetime);
          navigator.clipboard.writeText(reminderText).then(() => {
            alert(`✅ Appel programmé !\n\nRappel copié dans le presse-papier. Colle-le dans WhatsApp pour t'envoyer une notification.`);
          }).catch(() => {
            alert(`✅ Appel programmé pour ${lead.restaurant}`);
          });
        }
        fetchLeads();
      }
    } catch (err) {
      console.error('Failed to schedule call:', err);
      throw err;
    }
  };

  // Mark call outcome
  const markCallOutcome = async (leadId, outcome, notes = '') => {
    try {
      const res = await fetch(`${API_URL}/api/crm/leads/${leadId}/call-outcome`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ outcome, notes })
      });
      const data = await res.json();
      if (data.status === 'success') {
        fetchLeads();
        if (selectedLead?.id === leadId) {
          const updatedLead = leads.find(l => l.id === leadId);
          if (updatedLead) setSelectedLead(updatedLead);
        }
      }
    } catch (err) {
      console.error('Failed to mark call outcome:', err);
    }
  };

  // Open schedule modal for a lead
  const openScheduleModal = (lead) => {
    setScheduleModalLead(lead);
    setShowScheduleModal(true);
  };

  // Handle CRM notification actions
  const handleNotificationAction = (notification, action) => {
    if (action.action === 'whatsapp' && notification.lead) {
      const cleanPhone = notification.lead.phone?.replace(/[^0-9]/g, '') || '33745522450';
      const message = encodeURIComponent(`Salut ! C'est l'heure de notre appel pour ${notification.lead.restaurant}. Tu es dispo ?`);
      window.open(`https://wa.me/${cleanPhone}?text=${message}`, '_blank');
    } else if (action.action === 'copy_reminder' && notification.lead) {
      const reminderText = getWhatsAppReminderTemplate(notification.lead, notification.lead.scheduled_call?.datetime);
      navigator.clipboard.writeText(reminderText);
      alert('Rappel copié !');
    }
  };

  // Dismiss CRM notification
  const dismissCrmNotification = (notificationId) => {
    setCrmNotifications(prev => prev.filter(n => n.id !== notificationId));
  };

  // Call reminders hook
  useCallReminders(leads, (notification) => {
    setCrmNotifications(prev => [notification, ...prev.slice(0, 4)]);
  });

  // Add comment
  const addComment = async (leadId, author, text) => {
    try {
      const res = await fetch(`${API_URL}/api/crm/leads/${leadId}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ author, text })
      });
      const data = await res.json();
      if (data.comment) {
        const updatedLead = {
          ...selectedLead,
          comments: [...(selectedLead.comments || []), data.comment],
          last_action: `Note ajoutée par ${author}`,
          last_action_at: new Date().toISOString()
        };
        setSelectedLead(updatedLead);
        setLeads(prev => prev.map(l => l.id === updatedLead.id ? updatedLead : l));
      }
    } catch (err) {
      console.error('Failed to add comment:', err);
    }
  };

  // Delete lead
  const deleteLead = async (leadId) => {
    if (!window.confirm(`Supprimer ce lead ?`)) return;
    
    try {
      await fetch(`${API_URL}/api/crm/leads/${leadId}`, { method: 'DELETE' });
      setLeads(prev => prev.filter(l => l.id !== leadId));
      setSelectedLead(null);
      fetchStats();
    } catch (err) {
      console.error('Failed to delete lead:', err);
    }
  };

  if (!isUnlocked) {
    return <LockScreen onUnlock={() => setIsUnlocked(true)} />;
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.bg }}>
      {/* Top Navigation Bar */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            {/* Logo & Title */}
            <div className="flex items-center gap-3">
              <Link to="/" className="flex items-center gap-2">
                <span className="font-bebas text-xl" style={{ color: theme.ink }}>PRSNL</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ backgroundColor: theme.orange, color: '#FFF' }}>
                  CRM
                </span>
              </Link>
            </div>
            
            {/* Search (desktop) */}
            <div className="hidden md:flex flex-1 max-w-md mx-8">
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Rechercher un lead..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-full text-sm outline-none focus:border-orange-400 transition-colors"
                  data-testid="crm-search-input"
                />
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex items-center gap-3">
              {/* NOTIFICATIONS BUTTON + PANEL */}
              <div className="relative">
                <button 
                  onClick={() => setShowNotifications(!showNotifications)}
                  className={`relative p-2 rounded-full transition-colors ${
                    showNotifications ? 'bg-orange-100' : 'hover:bg-gray-100'
                  }`}
                  data-testid="notifications-btn"
                >
                  <Bell className={`w-5 h-5 ${notificationCount > 0 ? 'text-orange-500' : 'text-gray-500'}`} />
                  {notificationCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse">
                      {notificationCount > 9 ? '9+' : notificationCount}
                    </span>
                  )}
                </button>
                
                {/* Notifications Panel */}
                <AnimatePresence>
                  {showNotifications && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      className="absolute right-0 top-12 w-80 md:w-96 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden z-50"
                    >
                      <div className="p-3 border-b border-gray-100 flex items-center justify-between bg-gray-50">
                        <span className="font-bold text-gray-800">🔔 Notifications</span>
                        {notificationCount > 0 && (
                          <button 
                            onClick={markAllNotificationsRead}
                            className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                          >
                            Tout marquer lu
                          </button>
                        )}
                      </div>
                      
                      <div className="max-h-80 overflow-y-auto">
                        {notifications.length === 0 ? (
                          <div className="p-6 text-center text-gray-400">
                            <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">Aucune notification</p>
                          </div>
                        ) : (
                          notifications.map(notif => (
                            <div 
                              key={notif.id}
                              className="p-3 border-b border-gray-50 hover:bg-gray-50 transition-colors cursor-pointer"
                              onClick={() => {
                                // Find the lead and select it
                                const lead = leads.find(l => l.id === notif.lead_id);
                                if (lead) selectLead(lead);
                                markNotificationRead(notif.id);
                                setShowNotifications(false);
                              }}
                            >
                              <div className="flex items-start gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm ${
                                  notif.type === 'new_lead' ? 'bg-blue-500' :
                                  notif.type === 'status_change' ? 'bg-purple-500' :
                                  notif.type === 'activity' ? 'bg-green-500' :
                                  notif.type === 'followup' ? 'bg-amber-500' :
                                  'bg-gray-500'
                                }`}>
                                  {notif.type === 'new_lead' ? '🆕' :
                                   notif.type === 'status_change' ? '📊' :
                                   notif.type === 'activity' ? '📞' :
                                   notif.type === 'followup' ? '🔔' :
                                   notif.type === 'assignment' ? '👤' : '📌'}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-bold text-gray-800 truncate">{notif.lead_name}</p>
                                  <p className="text-xs text-gray-600">{notif.message}</p>
                                  <p className="text-[10px] text-gray-400 mt-1">
                                    {new Date(notif.created_at).toLocaleString('fr-FR', {
                                      day: 'numeric',
                                      month: 'short',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </p>
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    markNotificationRead(notif.id);
                                  }}
                                  className="p-1 hover:bg-gray-200 rounded"
                                >
                                  <X className="w-4 h-4 text-gray-400" />
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              
              {/* Push Notifications Toggle */}
              <button
                onClick={togglePushNotifications}
                className={`hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                  pushEnabled 
                    ? 'bg-green-100 text-green-600 hover:bg-green-200' 
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
                title={pushEnabled ? 'Notifications push activées' : 'Activer les notifications push'}
                data-testid="push-notifications-toggle"
              >
                <BellRing className={`w-4 h-4 ${pushEnabled ? 'animate-pulse' : ''}`} />
                {pushEnabled ? 'Push ON' : 'Push OFF'}
              </button>
              
              {/* AI PREDICTIONS BUTTON */}
              <div className="relative">
                <button
                  onClick={() => {
                    setShowAiPanel(!showAiPanel);
                    if (!showAiPanel && aiPredictions.length === 0) {
                      fetchAiPredictions();
                    }
                  }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                    showAiPanel
                      ? 'bg-purple-600 text-white'
                      : 'bg-gradient-to-r from-purple-100 to-indigo-100 text-purple-700 hover:from-purple-200 hover:to-indigo-200'
                  }`}
                  data-testid="ai-predictions-btn"
                >
                  <Brain className={`w-4 h-4 ${aiLoading ? 'animate-pulse' : ''}`} />
                  <span className="hidden md:inline">IA Closing</span>
                  <Sparkles className="w-3 h-3" />
                </button>
                
                {/* AI PREDICTIONS PANEL */}
                <AnimatePresence>
                  {showAiPanel && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      className="absolute right-0 top-12 w-96 md:w-[450px] bg-white rounded-xl shadow-2xl border border-purple-200 overflow-hidden z-50"
                    >
                      <div className="p-4 border-b border-purple-100 bg-gradient-to-r from-purple-50 to-indigo-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Brain className="w-5 h-5 text-purple-600" />
                            <span className="font-bold text-purple-800">🎯 Prédictions IA</span>
                          </div>
                          <button
                            onClick={fetchAiPredictions}
                            disabled={aiLoading}
                            className="px-3 py-1 rounded-lg text-xs font-bold bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1"
                          >
                            {aiLoading ? (
                              <>
                                <RefreshCw className="w-3 h-3 animate-spin" />
                                Analyse...
                              </>
                            ) : (
                              <>
                                <Sparkles className="w-3 h-3" />
                                Actualiser
                              </>
                            )}
                          </button>
                        </div>
                        {aiLastUpdated && (
                          <p className="text-[10px] text-purple-500 mt-1">
                            Dernière analyse: {new Date(aiLastUpdated).toLocaleString('fr-FR')}
                          </p>
                        )}
                      </div>
                      
                      <div className="max-h-[400px] overflow-y-auto">
                        {aiLoading && aiPredictions.length === 0 ? (
                          <div className="p-8 text-center">
                            <Brain className="w-12 h-12 text-purple-300 mx-auto mb-3 animate-pulse" />
                            <p className="text-sm text-gray-500">L'IA analyse vos leads...</p>
                            <p className="text-xs text-gray-400 mt-1">Cela peut prendre quelques secondes</p>
                          </div>
                        ) : aiPredictions.length === 0 ? (
                          <div className="p-6 text-center text-gray-400">
                            <Brain className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">Cliquez sur "Actualiser" pour lancer l'analyse</p>
                          </div>
                        ) : (
                          <>
                            <div className="p-3 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-100">
                              <p className="text-xs font-bold text-green-700">
                                🎯 Top {Math.min(5, aiPredictions.length)} leads à closer cette semaine
                              </p>
                            </div>
                            {aiPredictions.slice(0, 5).map((pred, idx) => (
                              <div 
                                key={pred.lead_id}
                                className="p-3 border-b border-gray-50 hover:bg-purple-50 transition-colors cursor-pointer"
                                onClick={() => {
                                  const lead = leads.find(l => l.id === pred.lead_id);
                                  if (lead) selectLead(lead);
                                  setShowAiPanel(false);
                                }}
                              >
                                <div className="flex items-start gap-3">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                                    pred.closing_probability >= 70 ? 'bg-green-500' :
                                    pred.closing_probability >= 50 ? 'bg-amber-500' :
                                    'bg-gray-400'
                                  }`}>
                                    #{idx + 1}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between">
                                      <p className="text-sm font-bold text-gray-800 truncate">{pred.restaurant}</p>
                                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                        pred.closing_probability >= 70 ? 'bg-green-100 text-green-700' :
                                        pred.closing_probability >= 50 ? 'bg-amber-100 text-amber-700' :
                                        'bg-gray-100 text-gray-600'
                                      }`}>
                                        {pred.closing_probability}% 🎯
                                      </span>
                                    </div>
                                    <p className="text-xs text-purple-600 font-medium mt-1">
                                      👉 {pred.recommended_action}
                                    </p>
                                    {pred.key_factors && pred.key_factors.length > 0 && (
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {pred.key_factors.slice(0, 2).map((factor, i) => (
                                          <span key={i} className="px-1.5 py-0.5 rounded text-[9px] bg-purple-100 text-purple-600">
                                            {factor}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                    {pred.personalized_pitch && (
                                      <p className="text-[10px] text-gray-500 mt-1 italic line-clamp-1">
                                        💬 "{pred.personalized_pitch}"
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                      
                      {aiPredictions.length > 0 && (
                        <div className="p-3 bg-gray-50 border-t border-gray-100">
                          <p className="text-[10px] text-gray-500 text-center">
                            {aiPredictions.length} leads analysés • Cliquez sur un lead pour voir les détails
                          </p>
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              
              {/* New leads indicator */}
              {statusCounts.new > 0 && (
                <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-orange-100 text-orange-600 text-sm font-bold animate-pulse">
                  <Bell className="w-4 h-4" />
                  {statusCounts.new} nouveau{statusCounts.new > 1 ? 'x' : ''}
                </div>
              )}
              
              {/* Calendar Button */}
              <button 
                onClick={() => setShowCalendar(!showCalendar)}
                className={`hidden md:flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                  showCalendar 
                    ? 'bg-purple-600 text-white' 
                    : 'bg-purple-50 text-purple-600 hover:bg-purple-100'
                }`}
                data-testid="calendar-btn"
              >
                <CalendarDays className="w-4 h-4" />
                Calendrier
                {leads.filter(l => l.scheduled_call).length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 rounded-full bg-white text-purple-600 text-[10px] font-black">
                    {leads.filter(l => l.scheduled_call).length}
                  </span>
                )}
              </button>
              
              {/* Sync Calendly */}
              <button 
                onClick={async () => {
                  try {
                    const res = await fetch(`${API_URL}/api/calendly/match-leads`);
                    const data = await res.json();
                    alert(`Synchronisation Calendly: ${data.matches?.length || 0} RDV trouvés`);
                    fetchLeads();
                  } catch (err) {
                    console.error('Calendly sync failed:', err);
                    alert('Erreur de synchronisation Calendly');
                  }
                }}
                className="hidden md:flex items-center gap-1 px-3 py-1.5 rounded-full bg-blue-50 text-blue-600 text-xs font-bold hover:bg-blue-100 transition-colors"
                data-testid="sync-calendly-btn"
              >
                <Calendar className="w-4 h-4" />
                Sync
              </button>
              
              {/* Email Settings Button */}
              <div className="relative">
                <button 
                  onClick={() => setShowEmailSettings(!showEmailSettings)}
                  className={`hidden md:flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                    showEmailSettings 
                      ? 'bg-indigo-600 text-white' 
                      : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'
                  }`}
                  data-testid="email-settings-btn"
                >
                  <Mail className="w-4 h-4" />
                  Emails
                </button>
                
                {/* Email Settings Panel */}
                <AnimatePresence>
                  {showEmailSettings && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      className="absolute right-0 top-12 w-80 bg-white rounded-xl shadow-2xl border border-indigo-200 overflow-hidden z-50"
                    >
                      <div className="p-4 border-b border-indigo-100 bg-gradient-to-r from-indigo-50 to-purple-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Mail className="w-5 h-5 text-indigo-600" />
                            <span className="font-bold text-indigo-800">📧 Emails Automatiques</span>
                          </div>
                          <button
                            onClick={() => setShowEmailSettings(false)}
                            className="p-1 hover:bg-indigo-100 rounded"
                          >
                            <X className="w-4 h-4 text-indigo-500" />
                          </button>
                        </div>
                      </div>
                      
                      <div className="p-4 space-y-3">
                        <label className="flex items-center justify-between cursor-pointer">
                          <span className="text-sm text-gray-700">📊 Emails par étape</span>
                          <input
                            type="checkbox"
                            checked={emailPrefs.send_stage_emails}
                            onChange={(e) => updateEmailPrefs({ ...emailPrefs, send_stage_emails: e.target.checked })}
                            className="w-5 h-5 rounded text-indigo-600"
                          />
                        </label>
                        
                        <label className="flex items-center justify-between cursor-pointer">
                          <span className="text-sm text-gray-700">⏰ Relance J+1</span>
                          <input
                            type="checkbox"
                            checked={emailPrefs.send_relance_j1}
                            onChange={(e) => updateEmailPrefs({ ...emailPrefs, send_relance_j1: e.target.checked })}
                            className="w-5 h-5 rounded text-indigo-600"
                          />
                        </label>
                        
                        <label className="flex items-center justify-between cursor-pointer">
                          <span className="text-sm text-gray-700">📆 Relance J+3</span>
                          <input
                            type="checkbox"
                            checked={emailPrefs.send_relance_j3}
                            onChange={(e) => updateEmailPrefs({ ...emailPrefs, send_relance_j3: e.target.checked })}
                            className="w-5 h-5 rounded text-indigo-600"
                          />
                        </label>
                        
                        <label className="flex items-center justify-between cursor-pointer">
                          <span className="text-sm text-gray-700">⚠️ Relance J+7</span>
                          <input
                            type="checkbox"
                            checked={emailPrefs.send_relance_j7}
                            onChange={(e) => updateEmailPrefs({ ...emailPrefs, send_relance_j7: e.target.checked })}
                            className="w-5 h-5 rounded text-indigo-600"
                          />
                        </label>
                        
                        <div className="border-t pt-3 mt-3">
                          <label className="flex items-center justify-between cursor-pointer">
                            <span className="text-sm text-gray-700">🔥 Alertes Hot Leads</span>
                            <input
                              type="checkbox"
                              checked={emailPrefs.send_hot_lead_alerts}
                              onChange={(e) => updateEmailPrefs({ ...emailPrefs, send_hot_lead_alerts: e.target.checked })}
                              className="w-5 h-5 rounded text-indigo-600"
                            />
                          </label>
                          
                          {emailPrefs.send_hot_lead_alerts && (
                            <div className="mt-2">
                              <label className="text-xs text-gray-500">Seuil d'alerte: {emailPrefs.hot_lead_threshold}%</label>
                              <input
                                type="range"
                                min="50"
                                max="95"
                                step="5"
                                value={emailPrefs.hot_lead_threshold}
                                onChange={(e) => updateEmailPrefs({ ...emailPrefs, hot_lead_threshold: parseInt(e.target.value) })}
                                className="w-full mt-1"
                              />
                            </div>
                          )}
                        </div>
                        
                        <button
                          onClick={triggerRelanceCheck}
                          className="w-full mt-3 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-sm font-bold rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-colors"
                        >
                          🚀 Lancer les relances maintenant
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              
              {/* Refresh */}
              <button 
                onClick={() => { fetchLeads(); fetchStats(); }}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                data-testid="crm-refresh-btn"
              >
                <RefreshCw className="w-5 h-5 text-gray-500" />
              </button>
              
              {/* Menu hamburger */}
              <button 
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors md:hidden"
              >
                <Menu className="w-5 h-5 text-gray-600" />
              </button>
              
              {/* Desktop links */}
              <div className="hidden md:flex items-center gap-2">
                <Link to="/stats" className="text-xs text-green-600 hover:text-green-700 px-3 py-2 font-bold flex items-center gap-1">
                  <BarChart3 className="w-4 h-4" />
                  Stats
                </Link>
                <Link to="/equipe" className="text-xs text-orange-600 hover:text-orange-700 px-3 py-2 font-bold flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  Équipe
                </Link>
                <Link to="/recrutement" className="text-xs text-gray-500 hover:text-gray-700 px-3 py-2">
                  Recrutement
                </Link>
                <Link to="/onboarding" className="text-xs text-gray-500 hover:text-gray-700 px-3 py-2">
                  Onboarding
                </Link>
              </div>
            </div>
          </div>
        </div>
        
        {/* Mobile search */}
        <div className="md:hidden px-4 pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-full text-sm outline-none focus:border-orange-400"
            />
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {showMobileMenu && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="fixed inset-x-0 top-14 z-40 bg-white border-b border-gray-200 shadow-lg md:hidden"
          >
            <div className="p-4 space-y-2">
              <Link to="/" className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50" onClick={() => setShowMobileMenu(false)}>
                <Home className="w-5 h-5 text-gray-500" />
                <span className="font-medium">Accueil</span>
              </Link>
              <Link to="/recrutement" className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50" onClick={() => setShowMobileMenu(false)}>
                <Users className="w-5 h-5 text-gray-500" />
                <span className="font-medium">Recrutement</span>
              </Link>
              <Link to="/onboarding" className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50" onClick={() => setShowMobileMenu(false)}>
                <FileText className="w-5 h-5 text-gray-500" />
                <span className="font-medium">Onboarding</span>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        
        {/* MINI DASHBOARD - Stats en temps réel */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <p className="text-[10px] uppercase opacity-80">📊 Leads aujourd'hui</p>
            <p className="text-2xl font-black">{statusCounts.today || 0}</p>
          </div>
          <div className="p-3 rounded-xl bg-gradient-to-br from-green-500 to-green-600 text-white">
            <p className="text-[10px] uppercase opacity-80">📅 RDV aujourd'hui</p>
            <p className="text-2xl font-black">{statusCounts.rdv_today || 0}</p>
          </div>
          <div className="p-3 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 text-white">
            <p className="text-[10px] uppercase opacity-80">📞 Taux contact</p>
            <p className="text-2xl font-black">{statusCounts.contact_rate || 0}%</p>
          </div>
          <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <p className="text-[10px] uppercase opacity-80">💰 Taux closing</p>
            <p className="text-2xl font-black">{statusCounts.closing_rate || 0}%</p>
          </div>
        </div>
        
        {/* BARRE DE FILTRES */}
        <div className="bg-white rounded-xl border border-gray-200 p-3 mb-4">
          <div className="flex flex-wrap items-center gap-2">
            {/* Mode Call Focus */}
            <button
              onClick={() => setCallFocusMode(!callFocusMode)}
              className={`px-4 py-2 rounded-lg text-sm font-bold transition-all flex items-center gap-2 ${
                callFocusMode 
                  ? 'bg-red-500 text-white shadow-lg shadow-red-500/30' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid="call-focus-btn"
            >
              📞 Mode Appel
              {callFocusMode && <span className="text-[10px] bg-white/20 px-1.5 py-0.5 rounded">ON</span>}
            </button>
            
            <div className="w-px h-6 bg-gray-200 mx-1" />
            
            {/* Filtres date */}
            <div className="flex rounded-lg border border-gray-200 overflow-hidden">
              {[
                { id: 'all', label: 'Tous' },
                { id: 'today', label: "Aujourd'hui" },
                { id: 'yesterday', label: 'Hier' },
                { id: '7days', label: '7 jours' },
                { id: '30days', label: '30 jours' }
              ].map(d => (
                <button
                  key={d.id}
                  onClick={() => setDateFilter(d.id)}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                    dateFilter === d.id 
                      ? 'bg-orange-500 text-white' 
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {d.label}
                </button>
              ))}
            </div>
            
            <div className="w-px h-6 bg-gray-200 mx-1" />
            
            {/* Tri */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-medium bg-white focus:outline-none focus:border-orange-400"
            >
              <option value="priority">🎯 Priorité</option>
              <option value="date">📅 Plus récent</option>
              <option value="score">🔥 Score (problèmes)</option>
              <option value="potential">💰 Potentiel €</option>
            </select>
            
            {/* Indicateur leads non traités */}
            {statusCounts.untreated > 0 && (
              <div className="ml-auto px-3 py-1.5 rounded-lg bg-red-100 text-red-700 text-xs font-bold animate-pulse">
                ⚠️ {statusCounts.untreated} non traités
              </div>
            )}
          </div>
        </div>
        
        {/* Stats Row - Filtres par statut */}
        <div className="flex gap-2 overflow-x-auto pb-4 mb-4 scrollbar-hide">
          <StatsCard 
            label="Total" 
            value={statusCounts.all} 
            icon={Users} 
            color={theme.ink}
            onClick={() => setFilter('all')}
            active={filter === 'all'}
          />
          <StatsCard 
            label="Nouveau" 
            value={statusCounts.nouveau || 0} 
            icon={Bell} 
            color="#3B82F6"
            onClick={() => setFilter('nouveau')}
            active={filter === 'nouveau'}
          />
          <StatsCard 
            label="À contacter" 
            value={statusCounts.a_contacter || 0} 
            icon={Phone} 
            color={theme.orange}
            onClick={() => setFilter('a_contacter')}
            active={filter === 'a_contacter'}
          />
          <StatsCard 
            label="Tentative" 
            value={statusCounts.tentative || 0} 
            icon={PhoneCall} 
            color={theme.amber}
            onClick={() => setFilter('tentative')}
            active={filter === 'tentative'}
          />
          <StatsCard 
            label="Discussion" 
            value={statusCounts.en_discussion || 0} 
            icon={MessageCircle} 
            color="#8B5CF6"
            onClick={() => setFilter('en_discussion')}
            active={filter === 'en_discussion'}
          />
          <StatsCard 
            label="RDV pris" 
            value={statusCounts.rdv_pris || 0} 
            icon={CalendarPlus} 
            color={theme.blue}
            onClick={() => setFilter('rdv_pris')}
            active={filter === 'rdv_pris'}
          />
          <StatsCard 
            label="RDV fait" 
            value={statusCounts.rdv_fait || 0} 
            icon={UserCheck} 
            color={theme.purple}
            onClick={() => setFilter('rdv_fait')}
            active={filter === 'rdv_fait'}
          />
          <StatsCard 
            label="Relance" 
            value={statusCounts.relance || 0} 
            icon={RefreshCw} 
            color={theme.amber}
            onClick={() => setFilter('relance')}
            active={filter === 'relance'}
          />
          <StatsCard 
            label="Closé" 
            value={statusCounts.gagne || 0} 
            icon={CheckCircle} 
            color={theme.green}
            onClick={() => setFilter('gagne')}
            active={filter === 'gagne'}
          />
          <StatsCard 
            label="Perdu" 
            value={statusCounts.perdu || 0} 
            icon={XCircle} 
            color={theme.red}
            onClick={() => setFilter('perdu')}
            active={filter === 'perdu'}
          />
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Lead List */}
          <div className={`lg:col-span-2 space-y-4 ${selectedLead ? 'hidden lg:block' : ''}`}>
            {loading ? (
              <div className="text-center py-12">
                <RefreshCw className="w-8 h-8 mx-auto animate-spin text-gray-400 mb-3" />
                <p className="text-gray-500">Chargement...</p>
              </div>
            ) : filteredLeads.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                <Users className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500">Aucun lead</p>
                <p className="text-xs text-gray-400 mt-1">Les diagnostics arriveront ici automatiquement</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredLeads.map(lead => (
                  <LeadCard
                    key={lead.id}
                    lead={lead}
                    onSelect={selectLead}
                    onStatusChange={updateStatus}
                    onUpdateField={updateField}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Lead Detail Panel */}
          <div className={`lg:col-span-1 ${!selectedLead ? 'hidden lg:block' : ''}`}>
            {!selectedLead ? (
              <div className="bg-white rounded-xl border border-gray-200 p-8 text-center sticky top-24">
                <Eye className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500 font-medium">Sélectionnez un lead</p>
                <p className="text-xs text-gray-400 mt-1">Cliquez sur un lead pour voir les détails</p>
              </div>
            ) : (
              <LeadDetailPanel
                lead={selectedLead}
                onClose={() => setSelectedLead(null)}
                onStatusChange={updateStatus}
                onWorkflowUpdate={updateWorkflow}
                onAddComment={addComment}
                onDelete={deleteLead}
                onUpdateField={updateField}
                teamMembers={teamMembers}
                onScheduleCall={openScheduleModal}
                onMarkCallOutcome={markCallOutcome}
              />
            )}
          </div>
        </div>
        
        {/* Calendar Panel */}
        <AnimatePresence>
          {showCalendar && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => setShowCalendar(false)}
            >
              <motion.div
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.95 }}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-5xl max-h-[90vh] overflow-y-auto"
              >
                <CalendarView
                  leads={leads}
                  onSelectLead={(lead) => {
                    selectLead(lead);
                    setShowCalendar(false);
                  }}
                  onScheduleCall={openScheduleModal}
                  onRefresh={fetchLeads}
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Schedule Call Modal */}
        <ScheduleCallModal
          lead={scheduleModalLead}
          isOpen={showScheduleModal}
          onClose={() => {
            setShowScheduleModal(false);
            setScheduleModalLead(null);
          }}
          onSchedule={scheduleCall}
        />
        
        {/* CRM Notifications */}
        <NotificationContainer
          notifications={crmNotifications}
          onDismiss={dismissCrmNotification}
          onAction={handleNotificationAction}
        />
      </main>
    </div>
  );
};

export default CRMPage;
